/**
 * The combinator DSL — typed TS builders that **return data** (SPEC.md §5.1).
 *
 * Nothing here executes anything. Every function assembles a node of the plain-data
 * `Ability` IR in `ir.ts`, which the interpreter walks. The TypeScript compiler is the first
 * reviewer: a mistyped selector, a missing argument or an effect used where a condition
 * belongs fails at build time rather than in a game.
 *
 * ```ts
 * // "Banish chosen character."
 * onPlay(banish(chosen.character()))
 *
 * // "Whenever this character quests, you may draw a card."
 * onQuest(may(draw(1)))
 *
 * // "{E}, 2 {I} — Deal 2 damage to chosen opposing character."
 * activated({ costs: [exertCost(), inkCost(2)] }, dealDamage(2, chosen.opposingCharacter()))
 * ```
 *
 * ## Targets
 *
 * `chosen.…()` returns a **placeholder**, not a `CardRef`. The effect builder it is handed to
 * lifts it into an enclosing `target` node and passes the binding down:
 *
 * ```ts
 * banish(chosen.character())
 * // ⇒ { kind: 'target', spec: { id: 'target', selector: { types: ['Character'] } },
 * //      then: { kind: 'banish', target: { kind: 'target', id: 'target' } } }
 * ```
 *
 * That is what makes a choice *inspectable*: the selector sits in the IR where
 * `legalActions` can evaluate it into a pool without running the effect. The binding id
 * defaults to `target`; an ability that chooses twice names the second one
 * (`chosen.character({ as: 'other' })`), and `defineAbilities` rejects a collision.
 *
 * ## Optionality
 *
 * `may(effect)` is a node, not a flag — declining means no part of that sentence happens
 * (CR §6.1.4), which falls out of the interpreter simply not walking the subtree.
 */
import type { CardTypeName } from '../cards.ts';
import type { GameEventType } from '../events.ts';
import type { InstanceId, PlayerIndex, Zone, ZonePlacement } from '../ids.ts';
import type { TurnPhase } from '../state.ts';
import type {
  Ability,
  ActionAbility,
  ActivatedAbility,
  ActivationCost,
  Amount,
  CardRef,
  ChallengeRole,
  Condition,
  CostApplies,
  DamageVerb,
  Duration,
  Effect,
  EventAmount,
  EventTemplate,
  KeywordAbility,
  KeywordName,
  PlayerPool,
  PlayerRef,
  PlayerTargetSpec,
  ReplacementAbility,
  Permission,
  PlayerPermission,
  ReplacementCondition,
  Restriction,
  Selector,
  Stat,
  StaticAbility,
  StaticEffect,
  TargetSpec,
  TriggerCondition,
  TriggeredAbility,
} from './ir.ts';

// ---------------------------------------------------------------------------
// Card references and target placeholders
// ---------------------------------------------------------------------------

/** A not-yet-lifted target. Handing one to an effect builder creates the choice. */
export interface Chosen {
  readonly __target: TargetSpec;
}

/** What every effect builder accepts where a card goes. */
export type Ref = CardRef | Chosen;

function isChosen(ref: Ref): ref is Chosen {
  return '__target' in ref;
}

/** The card whose ability this is. */
export const self: CardRef = { kind: 'self' };

/**
 * The card the triggering event was about — the quester, the banished card, the target.
 * Inside a continuous static or a guarded modifier it is **the card being read**, which is
 * what `theirWillpower(subject)` means in a `buff`.
 */
export const subject: CardRef = { kind: 'subject' };

/** A specific card by instance id. Mostly for tests; printed cards never name one. */
export function instance(id: InstanceId): CardRef {
  return { kind: 'instance', instance: id };
}

/** Read back a card bound by an earlier `chosen.…({ as })`. */
export function bound(id: string): CardRef {
  return { kind: 'target', id };
}

/**
 * Narrow a reference to just the cards satisfying a selector, evaluated when read — the
 * general primitive for intersecting a **multi-instance** binding with a `Selector` before an
 * effect consumes it (ticket 141). `bound('under-cards')` (ticket 104's under-pile a banish
 * leaves behind) can name several cards of any type; `matching(bound('under-cards'), { zone:
 * 'discard', types: ['Character'] })` is "of those, just the character cards" — what Sulley &
 * Boo - Scare Buddies's "you may play those characters" needs before a mass `playForFreeNow`
 * would otherwise sweep in the non-characters too. Works over any `CardRef`, not just a named
 * binding, single- or multi-instance alike.
 */
export function matching(ref: Ref, is: Selector): CardRef {
  return { kind: 'matching', of: refOf(ref), is };
}

/**
 * The card that **caused** the triggering event — the challenging character behind a challenge
 * banish, the ability that chose this card. Names nothing outside a trigger, which makes the
 * sentence it is in do nothing (CR §1.2.3).
 */
export const eventSource: CardRef = { kind: 'event-source' };

/**
 * The defender of the triggering `challenge-declared` — "banish that character" on a
 * challenger-side trigger (ticket 138). `challengedCardIs` is this same question asked as a
 * guard-only `Condition` to *filter* the trigger; this is the `CardRef` an *effect* resolves to
 * *act* on that same card. Names nothing outside a `challenge-declared` trigger (CR §1.2.3).
 */
export const eventDefender: CardRef = { kind: 'event-defender' };

/** The location a card is at (CR §4.3.6), or none. */
export function locationOf(of: CardRef = self): CardRef {
  return { kind: 'location-of', of };
}

/** The ability's controller. */
export const you: PlayerRef = { kind: 'controller' };

/** The next player in turn order — "each opponent" in a 2-player game. */
export const opponent: PlayerRef = { kind: 'opponent' };

export function player(index: PlayerIndex): PlayerRef {
  return { kind: 'player', player: index };
}

/** "…**its player** draws a card" — whoever controls a card the effect already named. */
export function controllerOf(ref: Ref): PlayerRef {
  return { kind: 'card-controller', of: refOf(ref) };
}

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/** Extra knobs every `chosen.…` builder takes. */
export interface ChooseOptions extends Selector {
  /** Binding name. Default `target`; name the second target of a two-target ability. */
  readonly as?: string;
  /**
   * How many to pick. Default 1. A literal for the common case; the computed forms are what
   * "the rest of what you are looking at" needs — `remainderInOrder` reads its own size this
   * way, since the remainder's count is not known until the ability resolves.
   */
  readonly count?: Amount;
  /** "up to N" — picking fewer, including none, is legal (CR §6.1.3). */
  readonly upTo?: boolean;
  /** "Chosen characters with total {S} N or less" — a cumulative cap across all picks. */
  readonly maxTotalStrength?: number;
  readonly label?: string;
  /** Who picks (`TargetSpec.chooser`). Default the ability's controller. */
  readonly chooser?: 'controller' | 'opponent';
}

function chooseSpec(base: Selector, options: ChooseOptions): Chosen {
  const { as, count, upTo, maxTotalStrength, label, chooser, ...rest } = options;
  const selector: Selector = { ...base, ...rest };
  const spec: TargetSpec = {
    id: as ?? 'target',
    selector,
    ...(count === undefined ? {} : { count }),
    ...(upTo === undefined ? {} : { upTo }),
    ...(maxTotalStrength === undefined ? {} : { maxTotalStrength }),
    ...(label === undefined ? {} : { label }),
    ...(chooser === undefined ? {} : { chooser }),
  };
  return { __target: spec };
}

/**
 * Targeting builders — "chosen character", "chosen opposing item", "another chosen
 * character". Every one produces a `Chosen` placeholder the effect builder lifts.
 */
export const chosen = {
  card(options: ChooseOptions = {}): Chosen {
    return chooseSpec({}, options);
  },
  character(options: ChooseOptions = {}): Chosen {
    return chooseSpec({ types: ['Character'] }, options);
  },
  /** "another chosen character" — excludes the ability's own source (CR §6.1.6). */
  otherCharacter(options: ChooseOptions = {}): Chosen {
    return chooseSpec({ types: ['Character'], excludeSelf: true }, options);
  },
  opposingCharacter(options: ChooseOptions = {}): Chosen {
    return chooseSpec({ types: ['Character'], controller: 'opponent' }, options);
  },
  yourCharacter(options: ChooseOptions = {}): Chosen {
    return chooseSpec({ types: ['Character'], controller: 'you' }, options);
  },
  damagedCharacter(options: ChooseOptions = {}): Chosen {
    return chooseSpec({ types: ['Character'], damaged: true }, options);
  },
  exertedCharacter(options: ChooseOptions = {}): Chosen {
    return chooseSpec({ types: ['Character'], exerted: true }, options);
  },
  item(options: ChooseOptions = {}): Chosen {
    return chooseSpec({ types: ['Item'] }, options);
  },
  location(options: ChooseOptions = {}): Chosen {
    return chooseSpec({ types: ['Location'] }, options);
  },
  characterOrLocation(options: ChooseOptions = {}): Chosen {
    return chooseSpec({ types: ['Character', 'Location'] }, options);
  },
};

// ---------------------------------------------------------------------------
// Player choices — "chosen player", "chosen opponent" (ticket 102)
// ---------------------------------------------------------------------------

/** A not-yet-lifted player choice. Handing one to `playerTargeting` creates the decision. */
export interface ChosenPlayer {
  readonly __playerTarget: PlayerTargetSpec;
}

/** Extra knobs `chosenPlayer()` takes — the player-scoped twin of `ChooseOptions`. */
export interface ChoosePlayerOptions {
  /** Binding name. Default `player`; name the second player choice of one ability. */
  readonly as?: string;
  /** Who is eligible. Default `any` — every player, including yourself. */
  readonly pool?: PlayerPool;
  /** Who picks. Default the ability's controller. */
  readonly chooser?: 'controller' | 'opponent';
  readonly label?: string;
}

/**
 * "Chosen player" (CR's own phrasing) — every player is eligible, including the ability's own
 * controller. Handing the result to `playerTargeting` opens the choice; a bare `chosenOpponent`
 * is `pool: 'opponent'`, CR's "chosen opponent".
 */
export function chosenPlayer(options: ChoosePlayerOptions = {}): ChosenPlayer {
  const { as, pool, chooser, label } = options;
  return {
    __playerTarget: {
      id: as ?? 'player',
      ...(pool === undefined ? {} : { pool }),
      ...(chooser === undefined ? {} : { chooser }),
      ...(label === undefined ? {} : { label }),
    },
  };
}

/** "Chosen opponent" — `chosenPlayer({ pool: 'opponent' })` under its own name. */
export function chosenOpponent(options: Omit<ChoosePlayerOptions, 'pool'> = {}): ChosenPlayer {
  return chosenPlayer({ ...options, pool: 'opponent' });
}

/**
 * Bind one player, then run `build` with the bound reference in scope — `targeting`'s player
 * counterpart. "Chosen opponent reveals their hand and discards a non-character card of your
 * choice" (Timon - Snowball Swiper) is one player choice feeding two sentences about them:
 *
 * ```ts
 * playerTargeting(chosenOpponent(), (who) => [
 *   revealCards({ zone: 'hand', controllerRef: who }),
 *   targeting(chosen.card({ zone: 'hand', controllerRef: who, types: [...] }), discardCard),
 * ])
 * ```
 */
export function playerTargeting(
  pick: ChosenPlayer,
  build: (who: PlayerRef) => Effect | readonly Effect[],
): Effect {
  const ref: PlayerRef = { kind: 'chosen', id: pick.__playerTarget.id };
  const built = build(ref);
  return {
    kind: 'choose-player',
    spec: pick.__playerTarget,
    then: Array.isArray(built) ? seq(...built) : (built as Effect),
  };
}

/**
 * "Each player …" (CR's own phrasing) — `playerTargeting`'s no-decision twin (ticket 125). Runs
 * `build` once per player, in turn order starting with the active player, each with an
 * independent `PlayerRef { kind: 'chosen', id }` bound to that iteration's player — the same
 * reference `playerTargeting` hands its callback, just assigned by iteration instead of by
 * choice. "ALLOW ME At the start of your turn, each player may draw a card" (Donald Duck -
 * Perfect Gentleman) — each player's own "may" is independent (the printed clarification: "each
 * player individually decides"), which is exactly `mayPlayer`, one `PlayerRef` reused as both
 * who is asked and who draws:
 *
 * ```ts
 * eachPlayer((who) => mayPlayer(who, {}, draw(1, who)))
 * ```
 */
export function eachPlayer(
  build: (who: PlayerRef) => Effect | readonly Effect[],
  options: { readonly as?: string } = {},
): Effect {
  const id = options.as ?? 'each-player';
  const ref: PlayerRef = { kind: 'chosen', id };
  const built = build(ref);
  return { kind: 'each-player', id, then: Array.isArray(built) ? seq(...built) : (built as Effect) };
}

/**
 * Choose once, then do **several things** to what was chosen.
 *
 * The effect builders each lift their own placeholder, which is right for the common
 * one-verb sentence — but "Chosen character gains Challenger +2 and Evasive this turn" is one
 * choice and two effects, and handing `chosen.character()` to both builders would ask the
 * player twice. This binds the choice once and hands the bound reference to a callback, so
 * everything it builds runs under the same `target` node:
 *
 * ```ts
 * targeting(chosen.character(), (card) => [
 *   grantKeyword('Challenger', card, { value: 2 }),
 *   grantKeyword('Evasive', card),
 * ])
 * ```
 */
export function targeting(
  pick: Chosen,
  build: (card: CardRef) => Effect | readonly Effect[],
): Effect {
  const ref: CardRef = { kind: 'target', id: pick.__target.id };
  const built = build(ref);
  return {
    kind: 'target',
    spec: pick.__target,
    then: Array.isArray(built) ? seq(...built) : (built as Effect),
  };
}

/** A bare selector, for `count()`, conditions and static abilities. */
export function cards(selector: Selector): Selector {
  return selector;
}

/** Every character you control. */
export function yourCharacters(extra: Selector = {}): Selector {
  return { types: ['Character'], controller: 'you', ...extra };
}

/** Every character an opponent controls. */
export function opposingCharacters(extra: Selector = {}): Selector {
  return { types: ['Character'], controller: 'opponent', ...extra };
}

/**
 * **This card only** — the selector a continuous static needs to talk about the card it is
 * printed on ("During your turn, this character gains Evasive"). An *applied* static says the
 * same thing with the `self` reference instead; this is the filter form.
 */
export function thisCard(extra: Selector = {}): Selector {
  return { self: true, ...extra };
}

/** Every character standing **here** — at this location, or at this character's (CR §5.6). */
export function charactersHere(extra: Selector = {}): Selector {
  return { types: ['Character'], here: true, ...extra };
}

/** Cards standing at the location this reference names (CR §4.3.6) — "here", but for a *chosen* one. */
export function atLocation(location: Ref, extra: Selector = {}): Selector {
  return { at: refOf(location), ...extra };
}

// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------

/** "for each …" — counted once, at resolution (CR §6.1.8). */
export function count(of: Selector): Amount {
  return { kind: 'count', of };
}

/**
 * "for each different ink type/symbol of cards revealed this way" (Colors of the Wind, ticket
 * 105) — the number of distinct printed inks across a pool, not the number of cards. Typically
 * `looking({ controller: 'any' })` over cards an earlier reveal just surfaced.
 */
export function distinctInkCount(of: Selector): Amount {
  return { kind: 'distinct-ink-count', of };
}

/** "equal to their {S}" — Support's amount. */
export function theirStrength(of: Ref = self): Amount {
  return { kind: 'strength-of', of: refOf(of) };
}

export function theirWillpower(of: Ref = self): Amount {
  return { kind: 'willpower-of', of: refOf(of) };
}

export function theirLore(of: Ref = self): Amount {
  return { kind: 'lore-of', of: refOf(of) };
}

/** "The same cost … as [that card]" — a card's own ink cost, read as a value. */
export function theirCost(of: Ref): Amount {
  return { kind: 'cost-of', of: refOf(of) };
}

export function damageOn(of: Ref = self): Amount {
  return { kind: 'damage-on', of: refOf(of) };
}

/** "for each 1 damage on opposing characters" — every counter in a pool, summed. */
export function damageTotal(of: Selector): Amount {
  return { kind: 'damage-total', of };
}

/**
 * The triggering event's own numeric value: an event `amount`, or the ink actually paid for a
 * `card-played` event. It is available both to a trigger guard and to its resolving effect.
 * `eventAmount()` is its counterpart for a replacement's produced event; this is for an
 * ordinary triggered ability that reacts to what already happened (Mulan - Elite Archer's
 * TRIPLE SHOT: deal that much damage again, elsewhere).
 */
export function triggeringAmount(): Amount {
  return { kind: 'triggering-amount' };
}

/**
 * The actual result an earlier step of **this same effect tree** bound via `as` —
 * `removeDamage`'s actual amount removed (after replacements and available damage), not the
 * requested amount ("Draw a card for each 1 damage removed this way", Ohana Means Family).
 * `triggeringAmount()`'s sequential counterpart: that reads the triggering event's own value,
 * this reads what a preceding sentence in the same resolution actually did. Unbound reads 0.
 */
export function boundAmount(id: string): Amount {
  return { kind: 'bound-amount', id };
}

/** The negation of an amount — "loses ¤ equal to that number", "pay 1 {I} less for each …". */
export function minus(of: Amount): Amount {
  return { kind: 'minus', of };
}

/** A scaled amount — "you pay **2** {I} less for each …" is `minus(times(2, count))`. */
export function times(n: number, of: Amount): Amount {
  return { kind: 'times', n, of };
}

/**
 * The lesser of an amount and a ceiling — "gain lore equal to her {S}, **to a maximum of** 6
 * lore" (Mulan - Resourceful Recruit) is `amountAtMost(theirStrength(), 6)`. `max` is usually a
 * literal but can itself be dynamic, matching `minus`/`times`'s generality. Named apart from
 * `atMost` (the `count-at-most` Condition over a Selector) since this caps an `Amount` instead.
 */
export function amountAtMost(of: Amount, max: Amount): Amount {
  return { kind: 'amount-at-most', of, max };
}

/**
 * The sum of two amounts — "cost **up to 2 more than** the banished character" is
 * `plus(theirCost(bound('cost-banished')), 2)`.
 */
export function plus(a: Amount, b: Amount): Amount {
  return { kind: 'plus', a, b };
}

/** "for each card under this character" — the size of its Boost pile (CR §8.3). */
export function cardsUnderCount(of: Ref = self): Amount {
  return { kind: 'cards-under', of: refOf(of) };
}

/** A player's lore total — read either side of a loss, its difference is the lore *lost*. */
export function loreTotal(who: PlayerRef = you): Amount {
  return { kind: 'player-lore', player: who };
}

/** How many challenges have been declared this turn (CR §4.6). */
export function challengesThisTurn(): Amount {
  return { kind: 'challenges-this-turn' };
}

/**
 * How many cards have been put into a player's inkwell this turn, the turn action and every
 * effect that puts one there alike — "if it's the **second** card you've put into your inkwell
 * this turn" is `amountEquals(cardsInkedThisTurn(), 2)`. The counter increments before
 * `card-inked` fires, so a guard sees the card that triggered it counted.
 */
export function cardsInkedThisTurn(who: PlayerRef = you): Amount {
  return { kind: 'cards-inked-this-turn', player: who };
}

/**
 * How many cards a player has drawn this turn, the draw step and every drawing effect alike
 * (`Player.cardsDrawnThisTurn`) — "how many cards you've drawn this turn" (Ink Amplifier, 2356).
 * The counter increments before `card-drawn` fires, so a guard sees the card that triggered it
 * counted, the same as `cardsInkedThisTurn`.
 */
export function cardsDrawnThisTurn(who: PlayerRef = you): Amount {
  return { kind: 'cards-drawn-this-turn', player: who };
}

/** How many cards have been put into a player's discard this turn, by any cause. */
export function cardsPutIntoDiscardThisTurn(who: PlayerRef = you): Amount {
  return { kind: 'cards-put-into-discard-this-turn', player: who };
}

/** Whether at least one card has been put into a player's discard this turn. */
export function anyCardsPutIntoDiscardThisTurn(who: PlayerRef = you): Condition {
  return amountMoreThan(cardsPutIntoDiscardThisTurn(who), 0);
}

/**
 * How many cards a player has **discarded** this turn (CR §7.3) — narrower than
 * `cardsPutIntoDiscardThisTurn`: only a hand→discard move counts, so "if you discarded a
 * card this turn" is not met by a character that left play for the discard by being banished.
 */
export function cardsDiscardedThisTurn(who: PlayerRef = you): Amount {
  return { kind: 'cards-discarded-this-turn', player: who };
}

/** Whether at least one card has been discarded (CR §7.3) by a player this turn. */
export function anyCardsDiscardedThisTurn(who: PlayerRef = you): Condition {
  return amountMoreThan(cardsDiscardedThisTurn(who), 0);
}

/**
 * How much damage has been removed from characters this turn. Pass a player for "from one of
 * your/their characters"; omit it for any character. `move` counts as removing from the donor,
 * while failed/zero removals do not increment the counter.
 */
export function damageRemovedThisTurn(who?: PlayerRef): Amount {
  return {
    kind: 'damage-removed-this-turn',
    ...(who === undefined ? {} : { player: who }),
  };
}

/**
 * How many cards matching this selector have been played this turn — "whenever you play a
 * **second** action in a turn" is `amountEquals(playedThisTurnCount({ types: ['Action'] }), 2)`.
 */
export function playedThisTurnCount(of: Selector): Amount {
  return { kind: 'played-this-turn-count', of };
}

/**
 * How many cards matching this selector were banished in a challenge this turn — the counting
 * form of `banishedInChallengeThisTurn`: "for each opposing character banished in a challenge
 * this turn" is this amount over `opposingCharacters()`, live until the Ready step resets it.
 */
export function banishedInChallengeThisTurnCount(of: Selector): Amount {
  return { kind: 'banished-in-challenge-this-turn-count', of };
}

/** "for each character that sang this song" — how many cards a binding names. */
export function boundCount(id: string): Amount {
  return { kind: 'bound-count', id };
}

/**
 * A placeholder used inside an `Amount` refers to a target bound elsewhere in the same
 * effect; it cannot open a choice of its own, so it reads as a plain binding.
 */
function refOf(ref: Ref): CardRef {
  return isChosen(ref) ? { kind: 'target', id: ref.__target.id } : ref;
}

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

export const always: Condition = { kind: 'always' };

export function not(of: Condition): Condition {
  return { kind: 'not', of };
}

export function allOf(...of: Condition[]): Condition {
  return { kind: 'all', of };
}

export function anyOf(...of: Condition[]): Condition {
  return { kind: 'any', of };
}

/** "if you have N or more …" */
export function atLeast(n: number, of: Selector): Condition {
  return { kind: 'count-at-least', of, n };
}

export function atMost(n: number, of: Selector): Condition {
  return { kind: 'count-at-most', of, n };
}

/** "if an opponent has more cards in their hand than you" — one pool strictly bigger. */
export function moreThan(of: Selector, than: Selector): Condition {
  return { kind: 'count-more-than', of, than };
}

/** "an opponent with more lore than you" — one `Amount` strictly bigger than another. */
export function amountMoreThan(of: Amount, than: Amount): Condition {
  return { kind: 'amount-more-than', of, than };
}

/**
 * "You have a character in play with more ¤ than each opposing character" — the live
 * counterpart to `custom()`'s one-shot version of the same max-vs-max comparison (Flynn Rider -
 * Frenemy's NARROW ADVANTAGE, The Sword Released's POWER APPOINTED), re-evaluated on every read
 * so it can drive a `whileActive` static (Faline - Playful Fawn's PRECOCIOUS FRIEND). Pass
 * `thisCard()` for `of` to say "this character's ¤ exceeds each opposing character" instead of
 * "you have *a* character" — the printed sentence `of` names decides which.
 */
export function strengthExceedsEach(of: Selector, than: Selector): Condition {
  return { kind: 'strength-exceeds-each', of, than };
}

/** "if chosen character is damaged" — a predicate on an already-bound card. */
export function cardIs(card: Ref, is: Selector): Condition {
  return { kind: 'card-matches', card: refOf(card), is };
}

/**
 * `cardIs`'s "any of" form, for a reference that can name several cards at once — "if any of
 * the cards that were under them are character cards" (Sulley & Boo - Scare Buddies, ticket
 * 141). `cardIs` itself only ever reads the first card a multi-instance reference names, which
 * is not this question.
 */
export function anyCardIs(card: Ref, is: Selector): Condition {
  return { kind: 'any-card-matches', card: refOf(card), is };
}

export function duringYourTurn(): Condition {
  return { kind: 'your-turn' };
}

/** "If this is your first turn" (CR §8.13) — the turn-relative half of Underdog's gate. */
export function duringYourFirstTurn(): Condition {
  return { kind: 'your-first-turn' };
}

/** "…you're the first player" — pair with `not()` for Underdog's "not the first player". */
export function wasStartingPlayer(): Condition {
  return { kind: 'starting-player' };
}

/** "While challenging a damaged character" — this card is the challenger and its target matches. */
export function challenging(is: Selector): Condition {
  return { kind: 'challenging', is };
}

/** Guard-only: the defender of the triggering challenge matches ("challenges a damaged …"). */
export function challengedCardIs(is: Selector): Condition {
  return { kind: 'event-defender', is };
}

/** Guard-only: the triggering event's player is an opponent of the ability's controller. */
export function byOpponent(): Condition {
  return { kind: 'event-player-is-opponent' };
}

/** Guard-only: the card that caused the triggering event is one of these types. */
export function causedBy(...types: CardTypeName[]): Condition {
  return { kind: 'event-source-type', types };
}

/**
 * Guard-only: the triggering banish or damage happened **in a challenge** (CR §4.6). `role`
 * narrows it to the side the event's card was on — `defender` is "is challenged and banished"
 * or "is dealt damage in a challenge"; on a `damage-dealt` event, pair it with `causedByCard()`
 * to ask about the *dealer's* side instead (Mulan - Elite Archer's TRIPLE SHOT).
 */
export function inChallenge(role?: ChallengeRole): Condition {
  return { kind: 'event-in-challenge', ...(role === undefined ? {} : { role }) };
}

/** Guard-only: the card that caused the triggering event is the one named. */
export function causedByCard(card: CardRef = self): Condition {
  return { kind: 'event-source-is', card };
}

/**
 * Guard-only: the ability that caused the triggering event has this printed name — what
 * "whenever one of your characters is chosen for Support" needs.
 */
export function causedByAbility(name: string): Condition {
  return { kind: 'event-ability-is', name };
}

/**
 * Guard-only: the triggering event happened at the location this ability is printed on —
 * "whenever a character is banished **while here**" (CR §5.6).
 */
export function eventHere(card: CardRef = self): Condition {
  return { kind: 'event-location-is', card };
}

/**
 * Guard-only: the character the triggering move was about **came from a location** (CR §4.3.6)
 * — "when you move a character here **from a location**", which must not fire for a character
 * that arrived at this location without ever having stood at another. `eventHere` reads where
 * the move went; this reads where it came from. `is` narrows which location it left.
 */
export function movedFromLocation(is?: Selector): Condition {
  return { kind: 'event-moved-from', ...(is === undefined ? {} : { is }) };
}

/**
 * Guard-only: the zone a `card-returned` event's card left (ticket 76) — `returnedFrom('discard')`
 * is "return … from your discard", `returnedFrom('play')` is the plain bounce Merlin -
 * Shapeshifter and Maleficent's Staff read. Without it the two are indistinguishable: both land
 * in a hand, and by the time a trigger sees the card it is already there.
 */
export function returnedFrom(zone: Zone): Condition {
  return { kind: 'event-returned-from', zone };
}

/** Characters that have (or have not) quested this turn (CR §4.5). */
export function questedThisTurn(quested = true): Selector {
  return { types: ['Character'], questedThisTurn: quested };
}

/** Characters that were (or were not) the defender of a challenge declared this turn (CR §4.6). */
export function wasChallengedThisTurn(challenged = true): Selector {
  return { types: ['Character'], wasChallengedThisTurn: challenged };
}

/**
 * Characters and locations that had (or did not have) a card put under them during the
 * current turn (`GameState.cardsPutUnderThisTurn`) — "if you've put a card under one of your
 * characters or locations this turn". Unlike `hasCardsUnder` (Boost/Shift's current pile
 * presence), this reads the *event* of a card going under this turn, matched by identity so a
 * pile that is later dissolved still counts for the rest of the turn. Combine with `controller`
 * and narrower `types` as the sentence needs — "under **her**" is `cardIs(self, …)`, "under
 * one of your characters or locations" is `atLeast(1, { ...cardPutUnderThisTurn(), controller:
 * 'you' })`.
 */
export function cardPutUnderThisTurn(putUnder = true): Selector {
  return { types: ['Character', 'Location'], cardPutUnderThisTurn: putUnder };
}

/**
 * Guard-only: which side of the challenge the event's card was on (CR §4.6) — the
 * `banished-in-challenge` form, where the role is a slot on the event itself.
 */
export function eventRole(role: ChallengeRole): Condition {
  return { kind: 'event-role', role };
}

/**
 * Guard-only: the event's card had a card under it (Boost's facedown pile or a Shift base, CR
 * §8.3, §8.10.4) **just before** it was banished — "if it had a card under it" (Merlin -
 * Completing His Research's LEGACY OF LEARNING). Reads the event's own snapshot rather than the
 * board, because `dissolveStack` (`stacks.ts`) has already scattered the pile by the time any
 * trigger it set off resolves.
 */
export function hadCardsUnder(): Condition {
  return { kind: 'event-had-cards-under' };
}

/**
 * Guard-only: an exerted character stood at the event's banished location **just before** it
 * left play — "if there was an exerted character here" (The Frozen Vine - Monstrous Plant's
 * PERSISTENT PROBLEM, ticket 104). Reads `card-banished`'s own snapshot rather than the board,
 * for the same reason as `hadCardsUnder`: `moveInstance` has already cleared every occupant's
 * `location` by the time any trigger it set off resolves.
 */
export function hadExertedCharacterHere(): Condition {
  return { kind: 'event-had-exerted-character-here' };
}

/** Guard-only: the other card the event names is this ability's own source. */
export function eventOtherIsSelf(): Condition {
  return { kind: 'event-other-is-self' };
}

/**
 * Guard-only: the other card the event names matches — "whenever **one of your characters**
 * banishes another character in a challenge".
 */
export function eventOtherIs(is: Selector): Condition {
  return { kind: 'event-other-is', is };
}

/** "If it's the second challenge this turn" — an amount equal to exactly this number. */
export function amountEquals(of: Amount, n: number): Condition {
  return { kind: 'amount-equals', of, n };
}

/** Guard-only: the card that banished the event's card matches — "banished by a Villain". */
export function banishedBy(is: Selector): Condition {
  return { kind: 'event-banisher', is };
}

/** "If 2 or more characters sang this song" — how many cards a binding names. */
export function boundAtLeast(id: string, n: number): Condition {
  return { kind: 'bound-at-least', id, n };
}

/**
 * "If an opposing character was banished in a challenge this turn" —
 * `banishedInChallengeThisTurn(opposingCharacters())`. Reads the per-turn fact
 * `GameState.banishedInChallengeThisTurn`, which the Ready step resets, so the condition is
 * live: true from the moment such a banish happens, false again as the next turn begins.
 */
export function banishedInChallengeThisTurn(of: Selector): Condition {
  return { kind: 'banished-in-challenge-this-turn', of };
}

/**
 * "If a character was banished this turn" — `banishedThisTurn({ types: ['Character'] })`. The
 * all-cause superset of `banishedInChallengeThisTurn`: reads `GameState.banishedThisTurn`,
 * written at the single `banish` chokepoint (`zones.ts`) so it counts challenge, ability,
 * lethal non-challenge damage, and cost-payment banishes alike. Live from the moment such a
 * banish happens, false again as the next turn's Ready step resets it.
 */
export function banishedThisTurn(of: Selector): Condition {
  return { kind: 'banished-this-turn', of };
}

/** "If you've played a song this turn" (CR §6.1.9). */
export function playedThisTurn(of: Selector): Condition {
  return { kind: 'played-this-turn', of };
}

/**
 * "…unless one of their characters has challenged this turn" — a card matching this selector
 * **declared a challenge** during the current turn (`GameState.challengedThisTurn`, reset in
 * the Ready step). The challenger side of `wasChallengedThisTurn`, and a condition rather than
 * a selector field for the same reason `banishedInChallengeThisTurn` is one: a challenger
 * banished in its own challenge is in a discard by the time anything asks.
 */
export function hasChallengedThisTurn(of: Selector): Condition {
  return { kind: 'challenged-this-turn', of };
}

/**
 * "If 1 or more cards have left a player's discard this turn" (The Horned King - Triumphant
 * Ghoul) — reads the per-turn fact `GameState.leftDiscardThisTurn`, which the Ready step
 * resets. Every leave-reason counts (returned to hand, played from discard, put under a card,
 * shuffled into a deck, …) and every player's discard counts, so the default selector `{}`
 * matches any card regardless of controller — the printed sentence names no restriction.
 */
export function cardsLeftDiscardThisTurn(of: Selector = {}): Condition {
  return { kind: 'left-discard-this-turn', of };
}

/**
 * "If one of your characters took damage this turn" (Devil's Eye Diamond) /
 * "if an opposing character took damage this turn" (John Silver - Vengeful Pirate) —
 * `damagedThisTurn(yourCharacters())` / `damagedThisTurn(opposingCharacters())`. Reads the
 * per-turn fact `GameState.damagedThisTurn`, written wherever `damage-dealt` fires for the
 * `deal` and `put` verbs (`damage.ts`'s `performDamage`) — a superset over **any** matching
 * character or location, not a specific card, which is what makes this a `Condition` rather
 * than a per-card accumulator. Live from the moment such damage lands, false again as the next
 * turn's Ready step resets it. Usable from a triggered-ability guard exactly like the other
 * this-turn conditions, and from a static's `while` (a cost modifier's own condition,
 * `costs.ts`'s `staticIsActive`) alike.
 */
export function damagedThisTurn(of: Selector): Condition {
  return { kind: 'damaged-this-turn', of };
}

/**
 * "You may pay 1 {I} to …" — whether a player could pay it at all (CR §4.3.2). `who` defaults
 * to the ability's controller; name somebody else for "…unless their player pays 3 {I}".
 *
 * The amount may be computed, which is how "you may play her" from a discard asks whether her
 * own cost is payable: `canPayInk(theirCost(self))`, never a hand-written number.
 */
export function canPayInk(amount: Amount, who?: PlayerRef): Condition {
  return { kind: 'can-pay-ink', amount, ...(who === undefined ? {} : { player: who }) };
}

// ---------------------------------------------------------------------------
// Effect control flow
// ---------------------------------------------------------------------------

export function noop(): Effect {
  return { kind: 'noop' };
}

/** Run these in written order (CR §6.7.2). Flattens single steps away. */
export function seq(...steps: Effect[]): Effect {
  const flat = steps.filter((step) => step.kind !== 'noop');
  if (flat.length === 0) return noop();
  if (flat.length === 1) return flat[0] as Effect;
  return { kind: 'seq', steps: flat };
}

/** "You may …" — declining means no part of the sentence happens (CR §6.1.4). */
export function may(...steps: Effect[]): Effect {
  return { kind: 'may', then: seq(...steps) };
}

/** "You may …", with the prompt text a UI shows. */
export function mayLabelled(label: string, ...steps: Effect[]): Effect {
  return { kind: 'may', then: seq(...steps), label };
}

/** What a `may` offered to a named player may say beyond "and then what". */
export interface OfferOptions {
  /** The printed "unless" — what happens when the offer is declined. */
  readonly otherwise?: Effect;
  /** Prompt text a UI shows. */
  readonly label?: string;
}

/**
 * "…**unless their player** …" — a "you may" put to a **specific player** rather than to the
 * ability's controller (CR §6.1.4), with what happens if they decline.
 *
 * `may()` always asks the controller, which is right for the ~200 cards that say "you may".
 * The offer a card makes to somebody else — the challenger's controller, the player whose
 * character it is — needs the decision to move without the ability changing hands, and that is
 * the whole of the difference: the pool, the amounts and the source are read exactly as they
 * would be otherwise.
 */
export function mayPlayer(who: PlayerRef, options: OfferOptions, ...steps: Effect[]): Effect {
  return {
    kind: 'may',
    then: seq(...steps),
    chooser: who,
    ...(options.otherwise === undefined ? {} : { otherwise: options.otherwise }),
    ...(options.label === undefined ? {} : { label: options.label }),
  };
}

/**
 * "…unless their player pays N {I}" (CR §6.1.7) — the pay-or-else sentence, as one combinator.
 *
 * Two halves, both ordinary IR: the `can-pay-ink` guard read against **that** player, so a
 * player who could never pay is not asked a question with one answer, and the offer itself,
 * whose decline branch is the consequence. Nothing is fused into the interpreter — this builds
 * `if(can-pay-ink → may(pay-ink) otherwise C, else C)`, so `legalActions` enumerates the offer
 * as the same `may:yes` / `may:no` pair every other yes/no raises, addressed to `who`.
 */
export function unlessPays(amount: number, who: PlayerRef, ...consequence: Effect[]): Effect {
  const otherwise = seq(...consequence);
  return ifThen(
    canPayInk(amount, who),
    mayPlayer(who, { otherwise, label: `Pay ${amount} ⬡` }, payInk(amount, who)),
    otherwise,
  );
}

/** "Choose one —" (CR §6.1.3). */
export function modal(...modes: { label: string; effect: Effect }[]): Effect {
  return { kind: 'modal', modes };
}

export function ifThen(condition: Condition, then: Effect, otherwise?: Effect): Effect {
  return {
    kind: 'if',
    condition,
    then,
    ...(otherwise === undefined ? {} : { otherwise }),
  };
}

/**
 * The escape hatch (SPEC.md §5.2) — names a synchronous generator in the `custom()`
 * registry. Ticket 13 builds the registry and the replay-to-resume machinery; until then
 * the interpreter raises a clear error when it reaches one.
 */
export function custom(id: string): Effect {
  return { kind: 'custom', id };
}

// ---------------------------------------------------------------------------
// Lifting targets
// ---------------------------------------------------------------------------

/**
 * Replace each placeholder with its binding and wrap the built node in one `target` node
 * per placeholder, outermost first — so the choices happen in argument order, then the
 * effect runs with every binding in scope.
 */
function withRefs(list: readonly Ref[], build: (refs: readonly CardRef[]) => Effect): Effect {
  const specs: TargetSpec[] = [];
  const resolved = list.map((ref) => {
    if (!isChosen(ref)) return ref;
    specs.push(ref.__target);
    return { kind: 'target', id: ref.__target.id } as CardRef;
  });
  let node = build(resolved);
  for (let i = specs.length - 1; i >= 0; i -= 1) {
    node = { kind: 'target', spec: specs[i] as TargetSpec, then: node };
  }
  return node;
}

function one(ref: Ref, build: (card: CardRef) => Effect): Effect {
  return withRefs([ref], (refs) => build(refs[0] as CardRef));
}

/**
 * Make a choice, then run several sentences about what was chosen.
 *
 * Every effect builder lifts a `chosen.…()` around **its own** node, which is all a one-verb
 * sentence needs. A card that chooses once and then says two things about the pick — "chosen
 * character gets +2 {S}; if a Villain character is chosen, they get +3 {S} instead" — needs the
 * `target` node to wrap the lot, and this is that:
 *
 * ```ts
 * choose(chosen.character(), ifThen(cardIs(bound('target'), { subtype: 'Villain' }),
 *   strengthBonus(3, bound('target')),
 *   strengthBonus(2, bound('target'))))
 * ```
 *
 * Pure sugar over the same `target` node — no new IR, and `bound(id)` reads the pick back.
 */
export function choose(ref: Chosen, ...then: Effect[]): Effect {
  return { kind: 'target', spec: ref.__target, then: seq(...then) };
}

// ---------------------------------------------------------------------------
// Effect verbs
// ---------------------------------------------------------------------------

export function gainLore(amount: Amount, who: PlayerRef = you): Effect {
  return { kind: 'gain-lore', player: who, amount };
}

export function loseLore(amount: Amount, who: PlayerRef = opponent): Effect {
  return { kind: 'lose-lore', player: who, amount };
}

export function draw(cardCount: Amount = 1, who: PlayerRef = you): Effect {
  return { kind: 'draw', player: who, count: cardCount };
}

/**
 * "Deal N damage to …" — reduced by Resist (CR §8.9, §1.9), unless `ignoresResist` says
 * otherwise ("This damage can't be reduced by Resist", Piercing Attack). Still a `deal`: dealt
 * damage triggers and the `damagedThisTurn` fact fire exactly as any other dealt damage.
 */
export function dealDamage(
  amount: Amount,
  target: Ref,
  options: { ignoresResist?: boolean } = {},
): Effect {
  return one(target, (card) => ({
    kind: 'deal-damage',
    target: card,
    amount,
    ...(options.ignoresResist === undefined ? {} : { ignoresResist: options.ignoresResist }),
  }));
}

/** "Put N damage counters on …" — bypasses damage modifiers (CR §1.9). */
export function putDamage(amount: Amount, target: Ref): Effect {
  return one(target, (card) => ({ kind: 'put-damage', target: card, amount }));
}

/**
 * `options.as` binds the actual amount removed — after clamping to what was there and any
 * replacements — for a later effect in the same tree to read via `boundAmount(id)`
 * ("Draw a card for each 1 damage removed this way").
 */
export function removeDamage(
  amount: Amount,
  target: Ref,
  options: { as?: string } = {},
): Effect {
  return one(target, (card) => ({
    kind: 'remove-damage',
    target: card,
    amount,
    ...(options.as === undefined ? {} : { as: options.as }),
  }));
}

/**
 * "Move N damage counters from A to B" (CR §1.9) — the fourth verb, and the one that is
 * neither dealing nor putting: no damage modifier applies and no "dealt damage" trigger sees
 * it. Damage never moves back onto the card it came from.
 */
export function moveDamage(amount: Amount, from: Ref, to: Ref): Effect {
  return withRefs([from, to], (refs) => ({
    kind: 'move-damage',
    from: refs[0] as CardRef,
    to: refs[1] as CardRef,
    amount,
  }));
}

/**
 * "Move N damage counters from each [matching] character to …" — the scoped form of
 * `moveDamage`: a pool of sources (evaluated once, as `deal-damage`'s `scope` is) each move
 * `amount` to the single `to`. E.g. "move 1 damage from each other character to them" (Morgana
 * Macawber - Reformed Spellcaster) reads as `moveDamageAll(1, { types: ['Character'],
 * excludeChosen: true }, bound('destination'))`.
 */
export function moveDamageAll(amount: Amount, fromScope: Selector, to: Ref): Effect {
  return one(to, (card) => ({
    kind: 'move-damage',
    fromScope,
    to: card,
    amount,
  }));
}

/** "Deal 2 damage to each opposing character" — the scoped form of `dealDamage`. */
export function dealDamageAll(amount: Amount, scope: Selector): Effect {
  return { kind: 'deal-damage', scope, amount };
}

/** "Remove 1 damage from each of your characters." `options.as` — see `removeDamage`. */
export function removeDamageAll(
  amount: Amount,
  scope: Selector,
  options: { as?: string } = {},
): Effect {
  return {
    kind: 'remove-damage',
    scope,
    amount,
    ...(options.as === undefined ? {} : { as: options.as }),
  };
}

/**
 * "Remove up to N damage total from any number of your characters" (ticket 135, I'm Never Not
 * by Your Side) — a shared budget split across any number of `scope` candidates, the split
 * itself a player decision. Unlike `removeDamageAll`, which applies `pool` to **every** pooled
 * character, this asks the controller to allocate `pool` **across** them, one at a time, and
 * stops once it is spent, the player declines to continue, or nobody eligible is left (CR
 * §1.2.3, §6.1.3).
 *
 * `options.as` binds the actual total removed for a later effect to read via `boundAmount(id)`
 * — the same convention `removeDamage`'s `as` follows (ticket 100), composed rather than
 * duplicated: "You gain 1 lore for each 1 damage removed this way" reads
 * `boundAmount('removed')` as its own sibling step.
 */
export function removeDamagePool(
  pool: Amount,
  scope: Selector,
  options: { as?: string; label?: string } = {},
): Effect {
  return {
    kind: 'distribute-remove-damage',
    spec: {
      id: 'distribute',
      selector: scope,
      pool,
      ...(options.label === undefined ? {} : { label: options.label }),
    },
    ...(options.as === undefined ? {} : { as: options.as }),
  };
}

export function banish(target: Ref): Effect {
  return one(target, (card) => ({ kind: 'banish', target: card }));
}

/** "Banish all opposing items." */
export function banishAll(scope: Selector): Effect {
  return { kind: 'banish', scope };
}

export function exert(target: Ref): Effect {
  return one(target, (card) => ({ kind: 'exert', target: card }));
}

export function ready(target: Ref): Effect {
  return one(target, (card) => ({ kind: 'ready', target: card }));
}

/**
 * "Ready all other characters here", "ready your other Princess characters" — a pool evaluated
 * once, as the effect resolves.
 */
export function readyAll(scope: Selector): Effect {
  return { kind: 'ready', scope };
}

/** "Exert all opposing characters" — the same shape. */
export function exertAll(scope: Selector): Effect {
  return { kind: 'exert', scope };
}

/**
 * "Return … to their player's hand" — a card in play, or one in a **discard** ("return a
 * character card with cost 2 or less from your discard to your hand"), which the target pool
 * already reaches.
 */
export function returnToHand(target: Ref): Effect {
  return one(target, (card) => ({ kind: 'return-to-hand', target: card }));
}

/** "Return all opposing characters to their players' hands." */
export function returnAllToHand(scope: Selector): Effect {
  return { kind: 'return-to-hand', scope };
}

/** "Put N damage counters on each …" — bypasses damage modifiers (CR §1.9). */
export function putDamageAll(amount: Amount, scope: Selector): Effect {
  return { kind: 'put-damage', scope, amount };
}

/**
 * "Put the top card of your deck facedown under this card" (CR §8.3) — what **Boost** does,
 * and what a card that puts cards under another one says. Not a draw and not a play: the card
 * is never in play and nobody may look at it (`stacks.ts`).
 */
export function putUnderFromDeck(target: Ref = self): Effect {
  return one(target, (card) => ({ kind: 'put-under-from-deck', target: card }));
}

/**
 * "Put a card from the top of your deck facedown under each of your other characters" —
 * Scrooge McDuck - Reformed Ebenezer's SPREADING JOY. `scope` is evaluated once, as the
 * effect resolves, into the ordered group Boost's put-under runs down one deck card at a
 * time; an undersized deck simply leaves the tail of the group untouched (CR §1.2.3).
 *
 * `options.as`, when given, binds the members that actually received a card — not the whole
 * snapshotted group — so a coupled follow-up ("those characters gain Ward…") can target
 * exactly the successful recipients via `bound(id)`.
 */
export function putUnderFromDeckEach(scope: Selector, options: { as?: string } = {}): Effect {
  return {
    kind: 'put-under-from-deck',
    scope,
    ...(options.as === undefined ? {} : { as: options.as }),
  };
}

/**
 * "Put a character card from your discard under this item faceup" (The Black Cauldron) — the
 * named-card put-under, and **faceup**: unlike `putUnderFromDeck`, nothing is hidden. `moved`
 * is the card that goes under; `under` is the card whose pile it joins (default `self`, "…
 * under this item").
 */
export function putCardUnder(moved: Ref, under: Ref = self): Effect {
  return withRefs([moved, under], (refs) => ({
    kind: 'put-card-under',
    target: refs[0] as CardRef,
    under: refs[1] as CardRef,
  }));
}

// -- moving a card between zones ---------------------------------------------

/** What `moveCard` may say beyond "which card, which zone". */
export interface MoveOptions {
  /** Decks are ordered: `bottom` is "on the bottom of your deck". */
  readonly placement?: ZonePlacement;
  readonly exerted?: boolean;
  /** "…facedown": knowledge is not granted, so nobody may look at it afterwards. */
  readonly facedown?: boolean;
  /** "…and shuffle it into their deck". */
  readonly shuffle?: boolean;
  /**
   * "…in a random order" — randomizes only the relative order of the cards **this effect
   * moves**, not the whole destination deck (ticket 103). Only meaningful with `moveCardAll`'s
   * `scope` form; a single-card `moveCard` has nothing to reorder.
   */
  readonly randomizeOrder?: boolean;
}

/**
 * The general zone verb. `banish` and `returnToHand` are the *named* rules actions with their
 * own triggers; this is the plain relocation every other clause is written in terms of.
 */
export function moveCard(target: Ref, to: Zone, options: MoveOptions = {}): Effect {
  return one(target, (card) => ({
    kind: 'move-card',
    target: card,
    to,
    ...(options.placement === undefined ? {} : { placement: options.placement }),
    ...(options.exerted === undefined ? {} : { exerted: options.exerted }),
    ...(options.facedown === undefined ? {} : { facedown: options.facedown }),
    ...(options.shuffle === undefined ? {} : { shuffle: options.shuffle }),
  }));
}

/** "Choose and discard a card", "discard this card." */
export function discardCard(target: Ref): Effect {
  return moveCard(target, 'discard');
}

/** "Put it on the top / on the bottom of your deck" (CR §7.2). */
export function putOnDeck(target: Ref, placement: ZonePlacement = 'top'): Effect {
  return one(target, (card) => ({ kind: 'put-on-deck', target: card, placement }));
}

/** "Shuffle a card from any discard into its player's deck." */
export function shuffleIntoDeck(target: Ref): Effect {
  return moveCard(target, 'deck', { shuffle: true });
}

/** The "each …" form of `moveCard`: "they discard their hands", "put the rest on the bottom". */
export function moveCardAll(scope: Selector, to: Zone, options: MoveOptions = {}): Effect {
  return {
    kind: 'move-card',
    scope,
    to,
    ...(options.placement === undefined ? {} : { placement: options.placement }),
    ...(options.exerted === undefined ? {} : { exerted: options.exerted }),
    ...(options.facedown === undefined ? {} : { facedown: options.facedown }),
    ...(options.shuffle === undefined ? {} : { shuffle: options.shuffle }),
    ...(options.randomizeOrder === undefined ? {} : { randomizeOrder: options.randomizeOrder }),
  };
}

/** "Look at the top 4 cards of your deck" — knowledge to the ability's controller only. */
export function lookAt(scope: Selector): Effect {
  return { kind: 'reveal', scope, to: 'controller' };
}

/** "Reveal a character card" — knowledge to everybody (CR §7.9). */
export function revealCards(scope: Selector): Effect {
  return { kind: 'reveal', scope, to: 'all' };
}

/** "Reveal **that** card" — the same act aimed at one card a choice already named. */
export function revealCard(target: Ref, to: 'controller' | 'all' = 'all'): Effect {
  return one(target, (card) => ({ kind: 'reveal', target: card, to }));
}

/** "Shuffle your deck" (CR §7.8). */
export function shuffle(who: PlayerRef = you): Effect {
  return { kind: 'shuffle', player: who };
}

/** "Pay 1 {I}" inside an effect — pair it with `canPayInk` to keep the offer legal. */
export function payInk(amount: Amount, who?: PlayerRef): Effect {
  return { kind: 'pay-ink', amount, ...(who === undefined ? {} : { player: who }) };
}

/**
 * "You may play that character for free" (CR §6.1.7) — from wherever it is now. Distinct from
 * `playForFree`, which is a payment *permission* for a future play action.
 *
 * A pool this chooses from is narrowed to cards that may actually be played (`playable`), so
 * a card whose own text forbids the play is never offered — the same answer the play itself
 * would give, one step earlier.
 *
 * `destination: 'bottom-of-deck'` is "if you do, put that card on the bottom of your deck
 * instead of into your discard" (Ursula - Deceiver of All's WHAT A DEAL, ticket 127) — an
 * **Action**'s own finishing destination, redirected for this one play. Threaded straight
 * through to the play, rather than chained as a later `putOnBottomOfDeck` step, because the
 * card's own clarification says the redirected card "is placed on the bottom of your deck
 * after its effect has finished" — it never visits the discard at all, which a later
 * "move it from discard" step could not say.
 */
export function playForFreeNow(
  target: Ref,
  options: { exerted?: boolean; destination?: 'bottom-of-deck' } = {},
): Effect {
  return one(playablePool(target), (card) => ({
    kind: 'play-free',
    target: card,
    ...(options.exerted === undefined ? {} : { exerted: options.exerted }),
    ...(options.destination === undefined ? {} : { destination: options.destination }),
  }));
}

/**
 * "Shift that character for free" — play it from wherever it is now onto one legal
 * single-card Shift base, paying no ink. Both decisions are ordinary declarative target
 * nodes: the first pool contains only playable cards with a legal base, and the second pool
 * is computed from the first binding through Shift's normal legality helper.
 *
 * Name `target` explicitly whenever the enclosing ability makes another choice, and use
 * `baseAs` to keep the generated base binding unique in that ability.
 */
export function shiftForFreeNow(
  target: Ref,
  options: { baseAs?: string } = {},
): Effect {
  const shiftable = shiftablePool(target);
  return one(shiftable, (card) => {
    const baseId = options.baseAs ?? 'shift-base';
    return {
      kind: 'target',
      spec: {
        id: baseId,
        selector: { controller: 'you', shiftBaseFor: card },
      },
      then: {
        kind: 'shift-free',
        target: card,
        onto: { kind: 'target', id: baseId },
      },
    };
  });
}

/**
 * "You may play her, and she enters play exerted" — the same play from wherever the card is
 * now, but for its **real printed cost** (CR §4.3.3, §6.1.7). `playForFreeNow`'s paying twin,
 * for the cards whose text never says "for free".
 *
 * The ink is taken as the effect resolves, so pair the offer with `canPayInk(theirCost(…))`:
 * a controller who cannot pay is not asked, and an unaffordable play that is somehow reached
 * anyway does nothing at all (CR §1.2.3). A pool this chooses from is narrowed to cards that
 * may be played *and* paid for (`playable`, `affordable`).
 */
export function playPayingNow(target: Ref, options: { exerted?: boolean } = {}): Effect {
  return one(payablePool(target), (card) => ({
    kind: 'play-paying',
    target: card,
    ...(options.exerted === undefined ? {} : { exerted: options.exerted }),
  }));
}

function playablePool(target: Ref): Ref {
  if (!isChosen(target)) return target;
  const spec = target.__target;
  return { __target: { ...spec, selector: { ...spec.selector, playable: true } } };
}

function shiftablePool(target: Ref): Ref {
  if (!isChosen(target)) return target;
  const spec = target.__target;
  return {
    __target: {
      ...spec,
      selector: { ...spec.selector, playable: true, shiftable: true },
    },
  };
}

function payablePool(target: Ref): Ref {
  if (!isChosen(target)) return target;
  const spec = target.__target;
  return {
    __target: { ...spec, selector: { ...spec.selector, playable: true, affordable: true } },
  };
}

/**
 * "Whenever one of your characters quests **this turn**, each opponent loses 1 lore" — a
 * delayed triggered ability an effect creates (CR §6.2.7).
 *
 * Takes a whole `TriggeredAbility` so the ordinary `on…` builders compose, and re-scopes its
 * condition to `anywhere`: the card that created it is usually an Action, already discarded.
 *
 * `closeOver` names bindings from the ability that is *creating* this delayed trigger — "play
 * a character for free. They gain Rush. At the end of your turn, banish **them**" needs its
 * `bound('target')` read (the character just played) to still resolve once the trigger fires,
 * long after that `target` node's own effect frame is gone. List the binding id(s) `effect`
 * reads with `bound(…)`; omit it for a delayed trigger that names no earlier choice (ticket
 * 106). Not automatic: a delayed effect that makes its own choice would have a stale forwarded
 * binding of the same id shadow the fresh one otherwise (`resolveCardRefAll` reads the first
 * match).
 */
export function delayedTrigger(
  ability: TriggeredAbility,
  duration: Duration = 'this-turn',
  options: { closeOver?: readonly string[] } = {},
): Effect {
  return {
    kind: 'delayed-trigger',
    when: { ...ability.when, where: 'anywhere' },
    effect: ability.effect,
    duration,
    ...(ability.name === undefined ? {} : { label: ability.name }),
    ...(options.closeOver === undefined || options.closeOver.length === 0
      ? {}
      : { closeOver: options.closeOver }),
  };
}

/** What a `modify` / `grant-keyword` effect may say beyond "how much, to whom". */
export interface ModifierOptions {
  /** Default `this-turn`. The turn boundary the record does not survive (CR §6.1.13). */
  readonly duration?: Duration;
  /** "while …" — re-evaluated on every read, so the bonus flips off and back on. */
  readonly guard?: Condition;
}

/** "Chosen character gets +2 {S} this turn." */
export function modify(
  change: { stat: Stat; amount: Amount; dynamic?: boolean } & ModifierOptions,
  target: Ref,
): Effect {
  return one(target, (card) => ({
    kind: 'modify',
    target: card,
    stat: change.stat,
    amount: change.amount,
    duration: change.duration ?? 'this-turn',
    ...(change.guard === undefined ? {} : { guard: change.guard }),
    ...(change.dynamic === undefined ? {} : { dynamic: change.dynamic }),
  }));
}

/**
 * "All opposing characters get -1 {S} this turn" — an **applied** static (CR §6.4.2.2).
 *
 * The scope is evaluated once, as the effect resolves, and frozen into the record: a
 * character played afterwards is *not* affected. A card in play whose text is continuous
 * ("Your other characters get +1 {S}") wants `buff` instead.
 */
export function modifyAll(
  scope: Selector,
  change: { stat: Stat; amount: Amount; dynamic?: boolean } & ModifierOptions,
): Effect {
  return {
    kind: 'modify',
    scope,
    stat: change.stat,
    amount: change.amount,
    duration: change.duration ?? 'this-turn',
    ...(change.guard === undefined ? {} : { guard: change.guard }),
    ...(change.dynamic === undefined ? {} : { dynamic: change.dynamic }),
  };
}

/** "Your characters gain Ward until the start of your next turn" — an applied static. */
export function grantKeywordAll(
  scope: Selector,
  keywordName: KeywordName,
  options: { value?: number } & ModifierOptions = {},
): Effect {
  return {
    kind: 'grant-keyword',
    scope,
    keyword: keywordName,
    ...(options.value === undefined ? {} : { value: options.value }),
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  };
}

/** Sugar for the overwhelmingly common `+N {S} this turn`. */
export function strengthBonus(amount: Amount, target: Ref, duration: Duration = 'this-turn'): Effect {
  return modify({ stat: 'strength', amount, duration }, target);
}

export function loreBonus(amount: Amount, target: Ref, duration: Duration = 'this-turn'): Effect {
  return modify({ stat: 'lore', amount, duration }, target);
}

export function willpowerBonus(
  amount: Amount,
  target: Ref,
  duration: Duration = 'this-turn',
): Effect {
  return modify({ stat: 'willpower', amount, duration }, target);
}

export function grantKeyword(
  keywordName: KeywordName,
  target: Ref,
  options: { value?: number } & ModifierOptions = {},
): Effect {
  return one(target, (card) => ({
    kind: 'grant-keyword',
    target: card,
    keyword: keywordName,
    ...(options.value === undefined ? {} : { value: options.value }),
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  }));
}

/** "…and loses Reckless this turn" (CR §6.6.5) — the mirror of `grantKeyword`. */
export function loseKeyword(
  keywordName: KeywordName,
  target: Ref = self,
  options: ModifierOptions = {},
): Effect {
  return one(target, (card) => ({
    kind: 'lose-keyword',
    target: card,
    keyword: keywordName,
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  }));
}

/** What a restriction may say beyond "who, and for how long". */
export interface RestrictOptions extends ModifierOptions {
  /** For `be-challenged`: which challengers are barred. */
  readonly by?: Selector;
}

/** "They can't quest for the rest of this turn" — a permission removed (CR §6.6.5). */
export function cannot(
  restriction: Restriction,
  target: Ref,
  options: RestrictOptions = {},
): Effect {
  return one(target, (card) => ({
    kind: 'restrict',
    target: card,
    restriction,
    ...(options.by === undefined ? {} : { by: options.by }),
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  }));
}

/** "Chosen character gains '[ability]' this turn" (CR §6.4.3). */
export function grantAbility(
  ability: Ability,
  target: Ref = self,
  options: ModifierOptions = {},
): Effect {
  return one(target, (card) => ({
    kind: 'grant-ability',
    target: card,
    ability,
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  }));
}

/** The same, over a scope evaluated once — "your characters gain '…' this turn". */
export function grantAbilityAll(
  scope: Selector,
  ability: Ability,
  options: ModifierOptions = {},
): Effect {
  return {
    kind: 'grant-ability',
    scope,
    ability,
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  };
}

/** "…gains the Detective classification until the start of your next turn" (CR §5.3.3.2). */
export function grantSubtype(
  subtype: string,
  target: Ref,
  options: ModifierOptions = {},
): Effect {
  return one(target, (card) => ({
    kind: 'grant-subtype',
    target: card,
    subtype,
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  }));
}

/** "Your characters with Reckless gain '⟳ — Gain 1 lore.'" — the continuous form. */
export function grantsAbility(filter: Selector, ability: Ability): StaticAbility {
  return staticAbility({ kind: 'grant-ability', filter, ability });
}

/** A temporary card permission — a specific ordinary legality limiter is lifted (CR §6.6.5). */
export function allow(
  permission: Permission,
  target: Ref,
  options: ModifierOptions = {},
): Effect {
  return one(target, (card) => ({
    kind: 'allow',
    target: card,
    permission,
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  }));
}

/**
 * The same, over a scope evaluated once — "your Floodborn characters … can challenge ready
 * characters this turn" (Attack of the Vine!). `cannotAll`'s permission-side mirror.
 */
export function allowAll(
  scope: Selector,
  permission: Permission,
  options: ModifierOptions = {},
): Effect {
  return {
    kind: 'allow',
    scope,
    permission,
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  };
}

/**
 * "This turn, you may ink an additional card" — a permission given to a **player** rather than
 * to a card (CR §6.6.5), so it needs no source in play to keep working. `who` is whose
 * permission it is, `amount` how many times over it is given.
 */
export function allowPlayer(
  permission: PlayerPermission,
  options: ModifierOptions & { who?: PlayerRef; amount?: number } = {},
): Effect {
  return {
    kind: 'allow-player',
    permission,
    ...(options.who === undefined ? {} : { player: options.who }),
    ...(options.amount === undefined ? {} : { amount: options.amount }),
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  };
}

/**
 * "This turn, you may ink an additional card." — the printed sentence, spelled as the
 * player-scoped permission `inkLimitOf` adds to the CR §4.2 limit. The card that says it is an
 * Action, so by the time the extra ink is taken the source is in the discard; the permission
 * lives on the player and expires with its duration, not with its card.
 */
export function mayInkAnother(amount = 1, options: ModifierOptions = {}): Effect {
  return allowPlayer('extra-ink', { ...options, amount });
}

/**
 * "Each opponent can't ready more than N of their characters at the start of their next
 * turn" (Marshmallow - Cranky Climber) — a numeric cap on the Ready step given to a
 * **player** rather than to a card (CR §6.6.5), the restriction-side mirror of `allowPlayer`.
 * `who` defaults to the ability's opponent, since every printed instance of this effect says
 * "each opponent".
 */
export function capReady(amount: number, options: ModifierOptions & { who?: PlayerRef } = {}): Effect {
  return {
    kind: 'cap-ready',
    amount,
    ...(options.who === undefined ? {} : { player: options.who }),
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  };
}

/** The same, over a scope evaluated once — "your other characters can't quest this turn". */
export function cannotAll(
  scope: Selector,
  restriction: Restriction,
  options: RestrictOptions = {},
): Effect {
  return {
    kind: 'restrict',
    scope,
    restriction,
    ...(options.by === undefined ? {} : { by: options.by }),
    duration: options.duration ?? 'this-turn',
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  };
}

// -- looking at, and rearranging, a deck (CR §7.9, §7.2) ---------------------

/**
 * "Look at the top N cards of your deck" — privately. The cards stay in the deck; what they
 * gain is that their owner now knows them *and where they are*, so the rest of the sentence
 * can name them with `looking()`.
 *
 * The set is cleared when the ability finishes, so a card never has to say "stop looking".
 */
export function lookAtDeck(cardCount: Amount, who: PlayerRef = you): Effect {
  return { kind: 'look-at-deck', player: who, count: cardCount };
}

/** "Reveal the top N cards of your deck" — the same act, done publicly (CR §7.9). */
export function revealTopOfDeck(cardCount: Amount, who: PlayerRef = you): Effect {
  return { kind: 'look-at-deck', player: who, count: cardCount, reveal: true };
}

/** Stop looking early. The end of the ability does this anyway. */
export function stopLooking(who: PlayerRef = you): Effect {
  return { kind: 'stop-looking', player: who };
}

/** The cards a player is currently looking at — the pool the rest of the sentence chooses from. */
export function looking(extra: Selector = {}): Selector {
  return { zone: 'deck', controller: 'you', looking: true, ...extra };
}

/** The card on top of a player's deck (CR §7.2). */
export function topOfDeck(who: PlayerRef = you): CardRef {
  return { kind: 'top-of-deck', player: who };
}

/** The card on the bottom of a player's deck — "draw a card from the bottom of your deck". */
export function bottomOfDeck(who: PlayerRef = you): CardRef {
  return { kind: 'bottom-of-deck', player: who };
}

/** "…and put it into your hand." */
export function putIntoHand(target: Ref): Effect {
  return one(target, (card) => ({ kind: 'put-into-hand', target: card }));
}

/** "Put it on the bottom of your deck." */
export function putOnBottomOfDeck(target: Ref): Effect {
  return one(target, (card) => ({ kind: 'put-on-deck', target: card, placement: 'bottom' }));
}

/** "Put it on the top of your deck." */
export function putOnTopOfDeck(target: Ref): Effect {
  return one(target, (card) => ({ kind: 'put-on-deck', target: card, placement: 'top' }));
}

/**
 * "Put the rest on the bottom of your deck in any order" (CR §7.2, §7.9) — a `looking()` (or
 * other already-known) pool's leftovers, placed one at a time in the order the player names
 * them, rather than the deck's own leftover order (Look at This Family's recorded
 * simplification does the latter; this is the genuine decision).
 *
 * "Any order" is a real choice, not a no-op: `moveCard`'s `bottom` placement pushes each pick
 * further down, so the first card named ends up shallowest and the last ends up truly at the
 * bottom. The pool's own size is what "the rest" means — `count(pool)`, read fresh when this
 * node opens — so the sentence needs no size of its own, and an emptied-out pool (nothing left
 * to place) asks nothing at all. Unlike a `target` node's *own* opening (SPEC.md's
 * single-candidate rule, which only fires for a fixed `count: 1`), a multi-pick session still
 * asks for its last card by name — the rule only ever skips the first ask, not the whole
 * session — so this always closes with as many real choices as the remainder is long. This is
 * what lets a dynamic look count ("equal to the number of cards under him") flow straight
 * through to its remainder without knowing, when the ability is written, how many cards that
 * will ever be.
 *
 * `as` names the binding (default `remainder`) — deliberately **not** `target`, the default
 * every other `chosen.…()` picks, because this almost always follows an earlier target in the
 * same ability ("you may reveal one, **then** place the rest") and two bindings sharing one id
 * silently shadow each other (`resolveCardRefAllIn` reads the first match, not the latest).
 */
export function remainderInOrder(
  pool: Selector,
  to: Zone,
  options: MoveOptions & { readonly as?: string } = {},
): Effect {
  const { as = 'remainder', ...move } = options;
  return targeting(chosen.card({ ...pool, count: count(pool), as }), (picked) =>
    moveCard(picked, to, move),
  );
}

/** What "put [card] into your inkwell" may say beyond which card. */
export interface InkwellOptions {
  /** Whose inkwell — "put that card into **your** inkwell". Default the controller. */
  readonly player?: PlayerRef;
  /** "…and exerted", which is how almost every card words it. Default `true`. */
  readonly exerted?: boolean;
  /**
   * "…facedown": knowledge is not granted, so a card inked off the top of a deck stays
   * unknown to *both* players (the Mickey Mouse - Detective clarification).
   */
  readonly facedown?: boolean;
}

/** "Put [card] into your inkwell facedown and exerted" (CR §7.5). */
export function putIntoInkwell(target: Ref, options: InkwellOptions = {}): Effect {
  return one(target, (card) => ({
    kind: 'put-into-inkwell',
    target: card,
    player: options.player ?? you,
    ...(options.exerted === undefined ? {} : { exerted: options.exerted }),
    ...(options.facedown === undefined ? {} : { facedown: options.facedown }),
  }));
}

/**
 * "Each opponent chooses and discards a card" (CR §7.3) — and "choose and discard a card",
 * which is the same effect aimed at yourself. The **named player** chooses.
 */
export function discardChosen(cardCount: Amount = 1, who: PlayerRef = opponent): Effect {
  return { kind: 'discard-chosen', player: who, count: cardCount };
}

/**
 * "…at random" — the random twin of `moveCardAll`: relocate this many cards from one of the
 * named player's zones, each picked by the engine's seeded RNG (SPEC.md §4.2) rather than by
 * anybody's choice, so a replay from the same seed moves the identical cards. "Return 2 cards
 * at random from your inkwell to your hand" is `moveRandom(2, 'inkwell', 'hand')`. Too small
 * a source zone moves as much as it can; an empty one moves nothing (CR §1.2.3).
 */
export function moveRandom(
  cardCount: Amount,
  from: Zone,
  to: Zone,
  who: PlayerRef = you,
  options: MoveOptions = {},
): Effect {
  return {
    kind: 'move-random',
    player: who,
    from,
    count: cardCount,
    to,
    ...(options.placement === undefined ? {} : { placement: options.placement }),
    ...(options.exerted === undefined ? {} : { exerted: options.exerted }),
    ...(options.facedown === undefined ? {} : { facedown: options.facedown }),
    ...(options.shuffle === undefined ? {} : { shuffle: options.shuffle }),
  };
}

/**
 * "…that player discards a card at random" (CR §7.3) — `moveRandom`'s hand→discard shape:
 * the seeded RNG picks from the named player's hand, so a replay from the same seed discards
 * the identical card. An empty hand discards nothing (CR §1.2.3).
 */
export function discardRandom(cardCount: Amount = 1, who: PlayerRef = opponent): Effect {
  return moveRandom(cardCount, 'hand', 'discard', who);
}

// -- locations (CR §4.3.6) ---------------------------------------------------

/** "Move [character] to [location]" — an effect's move, which pays no move cost. */
export function moveToLocation(target: Ref, location: Ref): Effect {
  return withRefs([target, location], (refs) => ({
    kind: 'move-to-location',
    target: refs[0] as CardRef,
    location: refs[1] as CardRef,
  }));
}

/**
 * "Opponents can't play actions until the start of your next turn" (CR §6.1.10) — a **play**
 * permission removed for a whole turn rather than for the cards on the board now, so it is a
 * payment record (`costs.ts`) rather than a `cannot` snapshot.
 */
export function forbidPlay(
  filter: Selector,
  options: { duration?: Duration; player?: PlayerRef } = {},
): Effect {
  return {
    kind: 'forbid-play',
    filter,
    duration: options.duration ?? 'this-turn',
    ...(options.player === undefined ? {} : { player: options.player }),
  };
}

// -- payment modifiers (the cost system, CR §4.3.2.2) ------------------------

/** What every payment-modifier builder accepts. */
export interface CostModifierOptions {
  /** Which payment it changes: `play` (default), `sing`, or an ability's `{I}`. */
  readonly applies?: CostApplies;
  /** How long it lasts. Default `this-turn`. */
  readonly duration?: Duration;
  /** Whose payments it changes. Default the ability's controller. */
  readonly player?: PlayerRef;
  /** "The **next** character you play this turn" — spent by the payment it applies to. */
  readonly oneShot?: boolean;
}

function costModifier(filter: Selector, amount: Amount, options: CostModifierOptions): Effect {
  return {
    kind: 'modify-cost',
    filter,
    amount,
    ...(options.applies === undefined ? {} : { applies: options.applies }),
    duration: options.duration ?? 'this-turn',
    ...(options.player === undefined ? {} : { player: options.player }),
    ...(options.oneShot === undefined ? {} : { oneShot: options.oneShot }),
  };
}

/**
 * "You pay N {I} less to play [filter] this turn."
 *
 * A literal number, because the IR stores the modifier as a signed amount and only an
 * increase can be computed at resolution: a "for each" *reduction* is `increaseCost` of a
 * negative computed amount, which is the seam to widen when a card needs one.
 */
export function reduceCost(
  amount: number,
  filter: Selector,
  options: CostModifierOptions = {},
): Effect {
  return costModifier(filter, -amount, options);
}

/** "Your opponents pay N {I} more to play [filter] this turn." */
export function increaseCost(
  amount: Amount,
  filter: Selector,
  options: CostModifierOptions = {},
): Effect {
  return costModifier(filter, amount, options);
}

/**
 * "Chosen character counts as having +3 cost to sing songs this turn" (Naveen's Ukulele) — a
 * payment modifier bound to the **card chosen as this resolved**, the same target/scope split
 * `modify` and `grantKeyword` already have.
 *
 * `reduceCost`/`increaseCost` take a criteria `Selector`, which is re-matched every time a
 * payment is priced: it would catch a second copy of the same character, and would let go of
 * the chosen one the moment its characteristics changed. This binds instead — the record names
 * instance ids, and nothing else can wander into it. Negative amounts reduce, as everywhere.
 */
export function modifyCostOf(
  amount: Amount,
  target: Ref,
  options: CostModifierOptions = {},
): Effect {
  return one(target, (card) => ({
    kind: 'modify-cost',
    target: card,
    amount,
    ...(options.applies === undefined ? {} : { applies: options.applies }),
    duration: options.duration ?? 'this-turn',
    ...(options.player === undefined ? {} : { player: options.player }),
    ...(options.oneShot === undefined ? {} : { oneShot: options.oneShot }),
  }));
}

/** "You may play [filter] for free this turn" (CR §6.1.7). */
export function playForFree(filter: Selector, options: CostModifierOptions = {}): Effect {
  return {
    kind: 'grant-free-play',
    filter,
    duration: options.duration ?? 'this-turn',
    ...(options.player === undefined ? {} : { player: options.player }),
    ...(options.oneShot === undefined ? {} : { oneShot: options.oneShot }),
  };
}

// ---------------------------------------------------------------------------
// Abilities
// ---------------------------------------------------------------------------

/**
 * An Action card's effect (CR §5.4): it resolves immediately and in full when the card is
 * played, never through the bag, and the card is discarded afterwards.
 *
 * ```ts
 * // Dragon Fire ▸ "Banish chosen character."
 * defineAbilities([actionEffect(banish(chosen.character()))])
 * ```
 */
export function actionEffect(...effect: Effect[]): ActionAbility {
  return { kind: 'action', effect };
}

/** A printed keyword. Card modules rarely write these — they generate from LorcanaJSON. */
export function keyword(name: KeywordName, value?: number, variant?: string): KeywordAbility {
  return {
    kind: 'keyword',
    keyword: name,
    ...(value === undefined ? {} : { value }),
    ...(variant === undefined ? {} : { variant }),
  };
}

/** Attach the small-caps name printed on the card. */
export function named<A extends Ability>(name: string, ability: A): A {
  return { ...ability, name };
}

/** The general triggered-ability builder; the `on…` helpers below are its common shapes. */
export function triggered(when: TriggerCondition, ...effect: Effect[]): TriggeredAbility {
  return { kind: 'triggered', when, effect };
}

/** Options every `on…` helper accepts — the trigger spec's window/guard/limit slots. */
export interface TriggerOptions {
  readonly window?: 'your-turn' | 'opponent-turn' | 'any-turn';
  readonly guard?: Condition;
  readonly limit?: 'once-per-turn' | 'twice-per-turn';
  /** Narrow which cards the event may be about ("whenever a *Broom* character …"). */
  readonly filter?: Selector;
  /** On damage, which verbs count (CR §1.9). Absent is the "takes damage" umbrella. */
  readonly damageVerb?: readonly DamageVerb[];
  /** On `phase-began`, which phase counts. `atStartOfTurn` is what sets it. */
  readonly phase?: TurnPhase;
  /** See `TriggerCondition.batch` (ticket 124) — merge repeat firings into one bag item. */
  readonly batch?: boolean;
}

function condition(
  event: GameEventType,
  subjectScope: TriggerCondition['subject'],
  where: NonNullable<TriggerCondition['where']>,
  options: TriggerOptions,
): TriggerCondition {
  return {
    event,
    subject: subjectScope,
    where,
    ...(options.filter === undefined ? {} : { filter: options.filter }),
    ...(options.damageVerb === undefined ? {} : { damageVerb: options.damageVerb }),
    ...(options.phase === undefined ? {} : { phase: options.phase }),
    ...(options.window === undefined ? {} : { window: options.window }),
    ...(options.guard === undefined ? {} : { guard: options.guard }),
    ...(options.limit === undefined ? {} : { limit: options.limit }),
    ...(options.batch === undefined ? {} : { batch: options.batch }),
  };
}

/** "When you play this character/item/location" (CR §4.3.4.1) — 45% of all triggers. */
export function onPlay(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('card-played', 'self', 'anywhere', {}), ...effect);
}

/** "Whenever you play a …" — someone else entering play under your control. */
export function onYouPlay(filter: Selector, ...effect: Effect[]): TriggeredAbility {
  return triggered(condition('card-played', 'controller', 'in-play', { filter }), ...effect);
}

/**
 * "When you play a [X] character on this card" (ticket 80) — a Shift base's own printed
 * ability reacting to the very Shift that buries it under a matching character. Anchored on
 * the `card-put-under` event that moves *this* card into `under`, with `where: 'own-shift'`
 * carrying the one-shot exception `bag.ts`'s `triggerMatches` gives that one event (CR
 * §4.3.4.8, §8.10.4) — see the `TriggerCondition.where` doc for the full reasoning. `filter`
 * describes the character landing on top, read off the event's source (CR §8.10's "the card
 * indicated" is what caused the burial, so it is what `eventSource` names here).
 */
export function onOwnShift(filter: Selector, ...effect: Effect[]): TriggeredAbility {
  return triggered(
    condition('card-put-under', 'self', 'own-shift', { guard: cardIs(eventSource, filter) }),
    ...effect,
  );
}

/** "Whenever this character quests" (CR §4.5) — 19% of all triggers. */
export function onQuest(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('character-quested', 'self', 'in-play', {}), ...effect);
}

/** "Whenever one of your other characters quests". */
export function onYourCharacterQuests(filter: Selector, ...effect: Effect[]): TriggeredAbility {
  return triggered(condition('character-quested', 'controller', 'in-play', { filter }), ...effect);
}

/** "Whenever this character challenges another character" (CR §4.6.3). */
export function onChallenge(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('challenge-declared', 'self', 'in-play', {}), ...effect);
}

/**
 * "When this character is banished" — registered `anywhere`, because by the time the bag
 * collects it the card is already in the discard (CR §7.6).
 */
export function onBanished(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('card-banished', 'self', 'anywhere', {}), ...effect);
}

/** "Whenever one of your characters is banished". */
export function onYourCardBanished(filter: Selector, ...effect: Effect[]): TriggeredAbility {
  return triggered(condition('card-banished', 'controller', 'anywhere', { filter }), ...effect);
}

/** "Whenever a card is banished" — every banish, whoever controlled the card. */
export function onAnyCardBanished(
  options: TriggerOptions,
  ...effect: Effect[]
): TriggeredAbility {
  return triggered(condition('card-banished', 'any', 'in-play', options), ...effect);
}

/** "Whenever this character moves to a location" (CR §4.3.6). */
export function onMove(options: TriggerOptions = {}, ...effect: Effect[]): TriggeredAbility {
  return triggered(condition('character-moved', 'self', 'in-play', options), ...effect);
}

/**
 * "Whenever a character moves here" — a location's own move trigger. `subject: 'any'` because
 * anyone's character may move to a location, and the `here` filter is what scopes it.
 */
export function onMoveHere(options: TriggerOptions = {}, ...effect: Effect[]): TriggeredAbility {
  return triggered(condition('character-moved', 'any', 'in-play', options), ...effect);
}

/**
 * "When this character is banished **in a challenge**" (CR §4.6.6) — the narrower event, which
 * an effect that banishes outright never meets.
 */
export function onBanishedInChallenge(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('banished-in-challenge', 'self', 'anywhere', {}), ...effect);
}

/**
 * "Whenever one of your other characters is banished in a challenge, …".
 *
 * The filter reads the banished card where it now is — the **discard** — so it is written
 * `{ zone: 'discard', … }`.
 */
export function onYourCardBanishedInChallenge(
  filter: Selector,
  ...effect: Effect[]
): TriggeredAbility {
  return triggered(condition('banished-in-challenge', 'controller', 'anywhere', { filter }), ...effect);
}

/**
 * "Whenever this character banishes another character in a challenge, …" — the *survivor's*
 * side of the same event, which `eventOtherIsSelf` is what picks out.
 */
export function onBanishesInChallenge(
  options: TriggerOptions,
  ...effect: Effect[]
): TriggeredAbility {
  const guard =
    options.guard === undefined
      ? eventOtherIsSelf()
      : allOf(eventOtherIsSelf(), options.guard);
  return triggered(
    condition('banished-in-challenge', 'any', 'anywhere', {
      ...options,
      filter: options.filter ?? { zone: 'discard', types: ['Character'] },
      guard,
    }),
    ...effect,
  );
}

/**
 * "Whenever **one of your characters** banishes another character in a challenge, …" — the
 * same event `onBanishesInChallenge` reads, asked about the whole board rather than about this
 * card, which is what `eventOtherIs` is for.
 */
export function onYourCharacterBanishesInChallenge(
  options: TriggerOptions,
  ...effect: Effect[]
): TriggeredAbility {
  const mine = eventOtherIs(yourCharacters());
  const guard = options.guard === undefined ? mine : allOf(mine, options.guard);
  return triggered(
    condition('banished-in-challenge', 'any', 'anywhere', {
      ...options,
      filter: options.filter ?? { zone: 'discard', types: ['Character'] },
      guard,
    }),
    ...effect,
  );
}

/** "Whenever this character is challenged" (CR §4.6.3) — the defender's side. */
export function onChallenged(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('card-challenged', 'self', 'in-play', {}), ...effect);
}

/**
 * "Whenever **a character of yours** is challenged, …" — the same defender's-side event
 * `onChallenged` reads, asked about the whole board rather than about this card. The ability's
 * own source is one of them, which is what the printed sentence says; the challenger is the
 * event's `event-source`, so the rest of the sentence can name it.
 */
export function onYourCharacterChallenged(
  options: TriggerOptions = {},
  ...effect: Effect[]
): TriggeredAbility {
  return triggered(
    condition('card-challenged', 'controller', 'in-play', {
      ...options,
      filter: options.filter ?? { types: ['Character'] },
    }),
    ...effect,
  );
}

/**
 * "When this character leaves play" — banished, returned to hand, put into an inkwell, put
 * under another card. Registered `anywhere`, because by the time it resolves the card is gone.
 */
export function onLeavesPlay(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('card-left-play', 'self', 'anywhere', {}), ...effect);
}

/** "Whenever you ready this character" — the Ready step, and any effect that readies it. */
export function onReadied(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('card-readied', 'self', 'in-play', {}), ...effect);
}

/**
 * "Whenever this character becomes exerted" (Fergus - Outpost Builder's JUST THE SPOT) —
 * questing, challenging, an activated ability's `{E}` cost, or any effect that exerts it
 * (`character-exerted`, `zones.ts`'s `exertCharacter`). Never fires for a character that
 * *enters* play already exerted (Bodyguard's choice, `enter-play-ready`) — it was never ready
 * to begin with.
 */
export function onCharacterExerted(
  options: TriggerOptions = {},
  ...effect: Effect[]
): TriggeredAbility {
  return triggered(condition('character-exerted', 'self', 'in-play', options), ...effect);
}

/**
 * "Whenever an opposing character becomes exerted" (Grimorum Arcanorum's DOCTRINA ADDUCERE) —
 * the same event as `onCharacterExerted`, scoped to the opponent's characters instead of the
 * ability's own card, which need not be a character itself (Grimorum Arcanorum is an Item).
 */
export function onOpposingCharacterExerted(
  options: TriggerOptions = {},
  ...effect: Effect[]
): TriggeredAbility {
  return triggered(condition('character-exerted', 'opponent', 'in-play', options), ...effect);
}

/**
 * "At the start of your turn" (CR §3.2) — the **Beginning phase only**. The `phase` slot is
 * load-bearing: `phase-began` is emitted at every phase boundary, so without it the ability
 * would resolve again in the main phase and again as the turn ended.
 */
export function atStartOfTurn(...effect: Effect[]): TriggeredAbility {
  return triggered(
    condition('phase-began', 'controller', 'in-play', {
      window: 'your-turn',
      phase: 'start-of-turn',
    }),
    ...effect,
  );
}

/**
 * "At the start of your turn, **if this card is in your discard**, …" — `atStartOfTurn` for an
 * ability that functions from the discard (CR §6.1.12, §3.2).
 *
 * The `where: 'discard'` scope is both halves of that clause: the card's triggers are
 * registered when it *enters* a discard rather than when it enters play, and the trigger only
 * fires while it is still there — so the printed "if this card is in your discard" needs no
 * guard of its own.
 */
export function atStartOfTurnInDiscard(...effect: Effect[]): TriggeredAbility {
  return triggered(
    condition('phase-began', 'controller', 'discard', {
      window: 'your-turn',
      phase: 'start-of-turn',
    }),
    ...effect,
  );
}

/**
 * "At the end of your turn" (CR §3.4).
 *
 * No `window` — CR-legal but subtle: `state.activePlayer` advances to the next player in the
 * same synchronous step that emits `turn-ended` (`engine.ts`'s `stepTurnEnd`), before
 * `collectTriggers` next runs, so a `window: 'your-turn'` guard here would read the *wrong*
 * turn's active player and never match. `subject: 'controller'` already asks the right
 * question — the event's own `player` field is fixed at emission, so it alone is "at the end
 * of *your* turn".
 */
export function atEndOfTurn(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('turn-ended', 'controller', 'in-play', {}), ...effect);
}

/**
 * "Whenever this character **takes** damage" — the umbrella reading (CR §1.9): dealt or put,
 * by any means. Moved damage is neither, and never meets it.
 */
export function onDamaged(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('damage-dealt', 'self', 'in-play', {}), ...effect);
}

/**
 * "Whenever this character is **dealt** damage" — the narrower reading, which "put N damage
 * counters on" does not meet.
 */
export function onDealtDamage(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('damage-dealt', 'self', 'in-play', { damageVerb: ['deal'] }), ...effect);
}

/** "Whenever you draw a card". */
export function onDraw(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('card-drawn', 'controller', 'in-play', {}), ...effect);
}

/**
 * "Whenever you return a [filter] card from your discard to your hand" (Fix-It Felix, Jr. -
 * Pint-Sized Hero, ticket 76) — the origin-specific half of `card-returned`: `returnedFrom`
 * is what keeps a Racer bounced back from *play* (an ordinary "return … to hand" effect) from
 * meeting this, since both land in the same hand and the event alone does not say which.
 */
export function onYouReturnFromDiscard(filter: Selector, ...effect: Effect[]): TriggeredAbility {
  return triggered(
    condition('card-returned', 'controller', 'in-play', { filter, guard: returnedFrom('discard') }),
    ...effect,
  );
}

/**
 * "Whenever this character sings a song" (CR §5.4.4, §8.12). One `card-sung` event is emitted
 * per singer, so a Sing Together played with three singers meets three copies of this.
 *
 * `options.guard` reads the sung song's own characteristics via `bound('song')` (ticket 127's
 * binding) — "…sings a song with Sing Together, draw a card" (4*Town - Hottest Band of the
 * Year, ticket 139) is `onSing({ guard: cardIs(bound('song'), { keyword: 'Sing Together' }) },
 * …)`.
 */
export function onSing(options: TriggerOptions = {}, ...effect: Effect[]): TriggeredAbility {
  return triggered(condition('card-sung', 'self', 'in-play', options), ...effect);
}

/** "Whenever one of your characters sings a song" — once per singer, like `onSing`. */
export function onYourCharacterSings(
  options: TriggerOptions = {},
  ...effect: Effect[]
): TriggeredAbility {
  return triggered(condition('card-sung', 'controller', 'in-play', options), ...effect);
}

/**
 * "Whenever one **or more** of your characters sings a song" — once per *song*, however many
 * sang it (CR §8.12). The singers ride in already bound, so the effect can say "you may ready
 * those characters" with `bound('singers')`.
 */
export function onSongSung(options: TriggerOptions = {}, ...effect: Effect[]): TriggeredAbility {
  return triggered(condition('song-sung', 'controller', 'in-play', options), ...effect);
}

/** "Whenever an opponent plays an action" (CR §4.3.1). */
export function onOpponentPlays(
  filter: Selector,
  options: TriggerOptions = {},
  ...effect: Effect[]
): TriggeredAbility {
  return triggered(
    condition('card-played', 'opponent', 'in-play', { ...options, filter }),
    ...effect,
  );
}

/** "Whenever you remove 1 or more damage from one of your characters" (CR §1.9). */
export function onDamageRemoved(
  options: TriggerOptions = {},
  ...effect: Effect[]
): TriggeredAbility {
  return triggered(condition('damage-removed', 'controller', 'in-play', options), ...effect);
}

/** "During your turn, whenever a card is put into your inkwell". */
export function onInk(...effect: Effect[]): TriggeredAbility {
  return triggered(condition('card-inked', 'controller', 'in-play', {}), ...effect);
}

/**
 * "Whenever a card leaves your discard" (Kristoff - Icy Explorer's STROKE OF LUCK, ticket 102)
 * — every way a card can leave a discard (put on a deck, played from there, put under a card,
 * shuffled into a deck, put into an inkwell), the trigger half of `GameState
 * .leftDiscardThisTurn`'s own "any cause" bookkeeping.
 */
export function onCardLeavesDiscard(
  options: TriggerOptions = {},
  ...effect: Effect[]
): TriggeredAbility {
  return triggered(condition('card-left-discard', 'controller', 'in-play', options), ...effect);
}

/**
 * "Whenever your opponent discards **1 or more** cards, …" (ticket 124, Prince John -
 * Greediest of All) — one bag item per discard *action*, however many cards it discarded, via
 * `TriggerCondition.batch` (`bag.ts`'s `addToBag`). `triggeringAmount()` reads the count in the
 * effect: "you may draw a card for each card discarded" is `may(draw(triggeringAmount()))`.
 */
export function onOpponentDiscards(
  options: TriggerOptions = {},
  ...effect: Effect[]
): TriggeredAbility {
  return triggered(
    condition('card-discarded', 'opponent', 'in-play', { ...options, batch: true }),
    ...effect,
  );
}

/**
 * "During your turn, when you discard this card, you may play this character from your
 * discard" (Mother Gothel - Evil as Ever) — an ability that **functions from the discard**
 * (CR §6.1.12), registered the instant this card lands there (`registerDiscardAbilitiesFor`,
 * `zones.ts`'s `moveInstance`) so it is already listening by the time the `card-discarded`
 * event that same move raises reaches the bag. `subject: 'self'` is exactly right here — the
 * card discarded and the card whose ability this is are the same object — unlike every other
 * `where: 'discard'` ability (`atStartOfTurnInDiscard`), which reacts to a *later* event while
 * merely sitting there.
 */
export function onDiscardedFromHand(...effect: Effect[]): TriggeredAbility {
  return triggered(
    condition('card-discarded', 'self', 'discard', { window: 'your-turn' }),
    ...effect,
  );
}

/**
 * "During your turn, whenever you discard a [filter] card" (Rapunzel & Flynn Rider - Unlikely
 * Pair's FRESH START) — the same `card-discarded` event read off the whole board by an
 * ordinary in-play ability, rather than by the discarded card's own (`onDiscardedFromHand`).
 */
export function onYouDiscard(filter: Selector, ...effect: Effect[]): TriggeredAbility {
  return triggered(
    condition('card-discarded', 'controller', 'in-play', { filter, window: 'your-turn' }),
    ...effect,
  );
}

/**
 * "When this character is chosen by an opponent's action" — the Vanish shape (CR §8.14),
 * and the general hook for "whenever an opponent chooses this card".
 */
export function onChosenByOpponent(...effect: Effect[]): TriggeredAbility {
  return triggered(
    condition('card-chosen', 'self', 'in-play', { guard: byOpponent() }),
    ...effect,
  );
}

// -- activation costs --------------------------------------------------------

/** `{E}` — exert the source. A character must be dry (CR §4.4.2); an item need not be. */
export function exertCost(): ActivationCost {
  return { kind: 'exert' };
}

/** `N {I}` — exert that many ready ink cards. */
export function inkCost(amount: number): ActivationCost {
  return { kind: 'ink', amount };
}

export function banishSelfCost(): ActivationCost {
  return { kind: 'banish-self' };
}

/**
 * "Banish 2 of your items —" (CR §4.4): a cost component paid by banishing cards you control.
 * Every legal set is its own activation option, so paying it is one keyed decision.
 */
export function banishCost(filter: Selector, count = 1): ActivationCost {
  return { kind: 'banish-cards', filter, ...(count === 1 ? {} : { count }) };
}

/**
 * "⟳ one of your characters —", "⟳ one of your items —" (CR §4.4): a cost component paid by
 * exerting cards you control. Mirrors `banishCost` — every legal set is its own activation
 * option. A character pays only when dry (CR §4.4.2); an item or location need only be ready.
 */
export function exertCardsCost(filter: Selector, count = 1): ActivationCost {
  return { kind: 'exert-cards', filter, ...(count === 1 ? {} : { count }) };
}

/**
 * "Discard a card —" (CR §4.4, §7.3): a cost component paid by discarding from hand, the card
 * chosen at payment time. Every legal choice is its own activation option, keyed by printed id
 * because a hand is hidden — and with too few cards in hand the activation is not offered.
 *
 * "Discard **an item card** —" narrows the legal choices with `filter`, mirroring
 * `banishCost`'s shape. Since the pool is always the hand rather than a selector-scoped zone,
 * the filter must say so itself — write `zone: 'hand'` on it, same as any other hand filter.
 */
export function discardCost(count = 1, filter?: Selector): ActivationCost {
  return {
    kind: 'discard-cards',
    ...(count === 1 ? {} : { count }),
    ...(filter === undefined ? {} : { filter }),
  };
}

/**
 * "Deal 1 damage to this character —" (CR §4.4): the source deals this much damage to itself
 * to pay the cost, subject to the normal lethal-damage banish check like any other damage.
 */
export function selfDamageCost(amount: number): ActivationCost {
  return { kind: 'self-damage', amount };
}

/**
 * "…may pay 3 lore —" (Hercules - Manipulated Hero's EVERYTHING HAS A PRICE, ticket 133): a
 * cost component paid by losing lore, through the same chokepoint any other lore loss goes
 * through (`loseLore`). Paid by whoever activates the ability — see `activated`'s `window` doc
 * — so a `your-turn` ability's cost comes out of the controller same as ever, while an
 * `opponent-turn` ability's comes out of the non-controller who is using it.
 */
export function payLoreCost(amount: number): ActivationCost {
  return { kind: 'pay-lore', amount };
}

/**
 * `[Cost] — [Effect]` (CR §6.3). Activated abilities never enter the bag (CR §7.7).
 *
 * `while` is "[condition], you may [cost] to [effect]": a live gate on the **activation
 * itself**, checked before any cost is paid — the activated-ability cousin of `whileActive`.
 * It must gate legality rather than sit in the effect, because a cost like `banishSelfCost()`
 * on a location changes the very board the condition reads before the effect ever runs.
 *
 * `window` mirrors a triggered ability's own window (`TriggerOptions.window`), plus what that
 * mirroring buys for free: `opponent-turn` (ticket 133) hands the whole ability to whoever is
 * **not** the controller — "During the opposing player's turn, they may [cost] to [effect]" —
 * rather than gating an ability the controller still owns. See `ActivatedAbility.window`'s doc
 * for the full three-way breakdown.
 */
export function activated(
  options: {
    costs: readonly ActivationCost[];
    limit?: 'once-per-turn';
    window?: 'your-turn' | 'any-turn' | 'opponent-turn';
    while?: Condition;
  },
  ...effect: Effect[]
): ActivatedAbility {
  return {
    kind: 'activated',
    costs: options.costs,
    effect,
    ...(options.limit === undefined ? {} : { limit: options.limit }),
    ...(options.window === undefined ? {} : { window: options.window }),
    ...(options.while === undefined ? {} : { while: options.while }),
  };
}

/** The common `{E} — …` shape. */
export function exertAbility(...effect: Effect[]): ActivatedAbility {
  return activated({ costs: [exertCost()] }, ...effect);
}

// -- static abilities --------------------------------------------------------

/**
 * A continuous static from a card in play (CR §6.4.2.1): computed on read, so a card that
 * arrives later is affected too — and one that stops matching the filter stops being
 * affected, with no bookkeeping either way.
 */
export function staticAbility(effect: StaticEffect): StaticAbility {
  return { kind: 'static', effect };
}

/**
 * "While …" — a live condition the whole static hangs on (CR §6.4.4), evaluated on every
 * characteristic read. Wraps a static the way `named` attaches a card's printed name:
 *
 * ```ts
 * // "While this character is exerted, your other characters get +1 {S}."
 * whileActive(cardIs(self, { exerted: true }), buff(yourCharacters({ excludeSelf: true }), 'strength', 1))
 * ```
 */
export function whileActive(condition: Condition, ability: StaticAbility): StaticAbility {
  return { ...ability, while: condition };
}

/**
 * "Your other Princess characters get +1 {S}."
 *
 * The amount may be an `Amount`: a static's "for each" is counted **continuously**, not once
 * (CR §6.1.8), and `theirWillpower(subject)` reads the {W} of the card being buffed — the
 * "+{S} equal to their {W}" family.
 */
export function buff(filter: Selector, stat: Stat, amount: Amount): StaticAbility {
  return staticAbility({ kind: 'buff', filter, stat, amount });
}

/**
 * "Your characters' {S} can't be reduced below their printed value" (Elisa Maza - Transformed
 * Gargoyle's FOREVER STRONG) — matching cards' `stat` never totals below its printed value,
 * read live by `hasPrintedFloor`. An ordinary bonus still applies normally; the floor only ever
 * stops a *reduction* from crossing the printed value.
 *
 * ```ts
 * // Elisa Maza - Transformed Gargoyle ▸ FOREVER STRONG
 * statFloor(yourCharacters(), 'strength')
 * ```
 */
export function statFloor(filter: Selector, stat: Stat): StaticAbility {
  return staticAbility({ kind: 'stat-floor', filter, stat });
}

/**
 * "You may have up to N copies of this card in your deck" (CR §3.1.2) — a **deck-construction**
 * rule. It is an ability of the card, so it is declared like one, but nothing in a running game
 * reads it: the deck validator does.
 */
export function deckLimit(max: number): StaticAbility {
  return staticAbility({ kind: 'deck-limit', max });
}

/**
 * "You can have [classification] characters in your deck regardless of ink type" (Christopher
 * Robin - Hunny Sage's GATHER THE PARTY) — `deckLimit`'s sibling: another **deck-construction**
 * rule (CR §3.1.2) nothing in a running game reads.
 */
export function deckInkFlexible(subtype: string): StaticAbility {
  return staticAbility({ kind: 'deck-ink-rule', subtype });
}

/**
 * "Your Princess characters cost 1 {I} less to play" — a continuous payment modifier.
 *
 * `from` is where this card must be for the ability to function (CR §6.1.12): `play` by
 * default, `hand` for an Underdog-shaped modifier, `discard` for "while this card is in your
 * discard, …". `amount` is negative to reduce, or positive for an additional cost.
 * `applies: 'quest' | 'challenge'` attaches that additional ink cost to each attempt by the
 * matching character. `mover` — `applies: 'move'` only — is matched against the **moving
 * character** rather than the destination: "your characters named Robin Hood may move here
 * for free". `onto` — `applies: 'shift'` only, `mover`'s onto-keyed twin — is matched against
 * the **chosen Shift base** rather than the card being played: "you pay 1 {I} less to shift a
 * character on top of this character".
 */
export function costsModified(
  filter: Selector,
  amount: Amount,
  options: {
    applies?: CostApplies;
    from?: Zone;
    mover?: Selector;
    onto?: Selector;
    limit?: 'once-per-turn';
  } = {},
): StaticAbility {
  return staticAbility({
    kind: 'cost-modifier',
    filter,
    amount,
    ...(options.applies === undefined ? {} : { applies: options.applies }),
    ...(options.from === undefined ? {} : { from: options.from }),
    ...(options.mover === undefined ? {} : { mover: options.mover }),
    ...(options.onto === undefined ? {} : { onto: options.onto }),
    ...(options.limit === undefined ? {} : { limit: options.limit }),
  });
}

/** "Your [filter] cards cost N {I} less to play." */
export function costsLess(
  amount: number,
  filter: Selector,
  options: {
    applies?: CostApplies;
    from?: Zone;
    mover?: Selector;
    onto?: Selector;
    limit?: 'once-per-turn';
  } = {},
): StaticAbility {
  return costsModified(filter, -amount, options);
}

/**
 * "Your [mover] characters may move here for free" — a mover-keyed move-cost reduction on the
 * location it is printed on. The reduction is the location's **own printed move cost**, read
 * at evaluation time rather than restated as a literal, so an errata to the printed number
 * cannot leave the card charging for a move it says is free.
 */
export function movesHereFree(mover: Selector): StaticAbility {
  return costsModified({ self: true }, minus(ownMoveCost()), { applies: 'move', mover });
}

/**
 * "Once during your turn, you pay N {I} less to move this character" (CR §6.1.13.2) —
 * mover-keyed on the card this is printed on (`mover: { self: true }`), against **any**
 * destination (an unrestricted `filter`), and limited to once per turn: the discount is spent
 * the moment a move it actually reduced goes through, tracked as a this-turn usage flag on the
 * source exactly like an activated ability's own once-per-turn limit, and it reads as fully
 * available again next turn.
 */
export function movesCheaperOnceThisTurn(amount: number): StaticAbility {
  return costsModified({}, -amount, {
    applies: 'move',
    mover: { self: true },
    limit: 'once-per-turn',
  });
}

/** The printed move cost of the card this ability is printed on (CR §4.3.6). */
export function ownMoveCost(): Amount {
  return { kind: 'own-move-cost' };
}

/**
 * "This character can't ⟳ to sing songs", "opponents can't play actions" — a continuous
 * restriction (CR §6.6.5). Wrap it in `whileActive` for the "unless …" and "while …" forms.
 */
export function restricts(
  filter: Selector,
  restriction: Restriction,
  options: { by?: Selector } = {},
): StaticAbility {
  return staticAbility({
    kind: 'restrict',
    filter,
    restriction,
    ...(options.by === undefined ? {} : { by: options.by }),
  });
}

/**
 * "Opponents must choose this character for actions and abilities if able" (ticket 119) —
 * a mandatory-target priority read by `targetPool`. `filter` defaults to `thisCard()`, the
 * printed shape; a card that names a different mandatory target would pass its own selector.
 */
export function mustBeChosen(filter: Selector = thisCard()): StaticAbility {
  return staticAbility({ kind: 'must-be-chosen', filter });
}

/**
 * A continuous "can" permission on matching cards (CR §6.6.5).
 *
 * `condition` narrows `challenge-ready` to "…can challenge ready **damaged** characters"
 * (Gizmoduck - Suited Up): matched against the potential challenge target, not against
 * `filter`'s source cards. Permissions without a target condition, including
 * `quest-while-drying`, apply directly to the matching card.
 */
export function allows(
  filter: Selector,
  permission: Permission,
  options: { condition?: Selector } = {},
): StaticAbility {
  return staticAbility({
    kind: 'allow',
    filter,
    permission,
    ...(options.condition === undefined ? {} : { condition: options.condition }),
  });
}

/**
 * "You may ink an additional card" — raises your CR §4.2 per-turn ink limit by `amount`
 * while this card is in play, read live by `inkLimitOf`. Wrap it in
 * `whileActive(duringYourTurn(), …)` for the printed "During your turn" guard.
 */
export function extraInk(amount = 1): StaticAbility {
  return staticAbility({ kind: 'extra-ink', amount });
}

/**
 * "All cards in your hand count as having ◉" — every card in your hand passes the CR §4.2
 * inkwell-symbol check while this card is in play, read live by `handCountsAsInkable`.
 * Nothing else about inking changes: the per-turn limit and the facedown placement are
 * untouched.
 */
export function handInkable(): StaticAbility {
  return staticAbility({ kind: 'hand-inkable' });
}

/**
 * "You can ink cards from your discard" — adds your discard pile as a legal origin for the
 * ordinary once-per-turn ink action while this card is in play, read live by
 * `discardCountsAsInkable`. The printed inkwell symbol is still required; this adds a zone,
 * not a symbol override, exactly as `handInkable` leaves the per-turn limit untouched.
 */
export function discardInkable(): StaticAbility {
  return staticAbility({ kind: 'discard-inkable' });
}

/**
 * "Each player plays with the top card of their deck face up" — the covered decks' top card
 * is continuously revealed to everyone while this card is in play, read live by
 * `deckTopIsRevealed`. The reveal follows the deck: a draw or a shuffle simply puts a new
 * card on top, face up, and everything is face down again the moment this card leaves play.
 * `decks: 'your'` narrows it to this card's controller ("you play with …").
 */
export function deckTopRevealed(decks: 'your' | 'each' | 'opponent' = 'each'): StaticAbility {
  return staticAbility({ kind: 'reveal-deck-top', decks });
}

/**
 * "Skip your turn's Draw step" (Arthur - Determined Squire) — suppresses the controller's
 * CR §3.2.3 Draw step while this card is in play, read live by `drawStepSkipped`. Always the
 * controller's own step; there is no printed card that spells this out for opponents.
 */
export function skipsDrawStep(): StaticAbility {
  return staticAbility({ kind: 'skip-draw' });
}

/**
 * "During challenges, your characters deal damage with their {W} instead of their {S}" —
 * changes the characteristic used by the challenge Damage step for this card's controller.
 * The static is live and player-scoped: it covers both challengers and defenders, and stops
 * applying the instant its source leaves play.
 */
export function challengeDamageUses(stat: 'willpower'): StaticAbility {
  return staticAbility({ kind: 'challenge-damage-stat', stat });
}

/**
 * "Each opposing player can't gain lore unless one of their characters has challenged this
 * turn" — a **player-scoped** "can't" (CR §6.6.5), read live by `canGainLore` at the one
 * chokepoint every lore gain passes through, so questing, a location's ◊ and an effect's
 * "gain N lore" are stopped alike.
 *
 * `unless` is the printed exception, evaluated from the **restricted player's** seat — "you"
 * is them, not this card's controller. The "While this character is exerted" half is an
 * ordinary `whileActive` guard:
 *
 * ```ts
 * // Peter Pan – Never Land Prankster ▸ CAN'T TAKE A JOKE?
 * whileActive(
 *   cardIs(self, { exerted: true }),
 *   cannotGainLore('opponents', hasChallengedThisTurn(yourCharacters())),
 * )
 * ```
 */
export function cannotGainLore(
  players: 'you' | 'opponents' | 'each',
  unless?: Condition,
): StaticAbility {
  return staticAbility({
    kind: 'restrict-lore-gain',
    players,
    ...(unless === undefined ? {} : { unless }),
  });
}

/**
 * "Opponents need 25 lore to win the game" — raises the CR §1.8.1.1 lore-to-win threshold for
 * `players` by `amount`, read live by `loreToWinOf`. Player-scoped like `extraInk`, so it has
 * no card filter — each copy adds its own `amount` on top of `state.rules.loreToWin`.
 */
export function raisesLoreToWin(
  players: 'you' | 'opponents' | 'each',
  amount: number,
): StaticAbility {
  return staticAbility({ kind: 'raise-lore-to-win', players, amount });
}

/** "This character enters play exerted." */
export function entersPlayExerted(): StaticAbility {
  return restricts({ self: true }, 'enter-play-ready');
}

/** "This character also counts as being named [name] for Shift" (CR §8.10.3). */
export function countsAsNamedForShift(name: string, filter: Selector = { self: true }): StaticAbility {
  return staticAbility({ kind: 'shift-name', filter, name });
}

/**
 * "You may play any character with Shift on this character as if this character had any name"
 * (Morph - Space Goo's MIMICRY) — the wildcard sibling of `countsAsNamedForShift`: satisfies
 * every name a Shift card's base could ask for, rather than one specific name.
 */
export function countsAsAnyNameForShift(filter: Selector = { self: true }): StaticAbility {
  return staticAbility({ kind: 'shift-wildcard-base', filter });
}

/**
 * "You can shift any character on top of this character. (This includes all Shift variants.)"
 * (Morph - Little Imitator's ADVANCED MIMICRY) — the broader sibling of `countsAsAnyNameForShift`:
 * waives Shift base eligibility outright rather than only satisfying a name, so
 * `[Classification] Shift` also lands here regardless of this card's own subtypes.
 * `Universal Shift` already accepts any base and `Potato Shift` only ever targets items, so
 * neither variant is affected by this static either way.
 */
export function acceptsAnyShiftVariant(filter: Selector = { self: true }): StaticAbility {
  return staticAbility({ kind: 'shift-any-base', filter });
}

/**
 * "You may banish chosen item of yours to play this character for free" — an alternate cost
 * (CR §1.5.5), enumerated by `payments.ts` as its own `play:d…:banish:i…` option.
 */
export function alternateCostBanish(
  banishable: Selector,
  options: { count?: number; window?: 'your-turn' | 'any-turn'; for?: 'play' | 'shift' } = {},
): StaticAbility {
  return staticAbility({
    kind: 'alternate-cost',
    payment: { kind: 'banish', filter: banishable },
    ...(options.count === undefined ? {} : { count: options.count }),
    ...(options.window === undefined ? {} : { window: options.window }),
    ...(options.for === undefined ? {} : { for: options.for }),
  });
}

/**
 * "You may exert 4 items of yours to play this character for free" (Scrooge McDuck -
 * Resourceful Miser's PUT IT TO GOOD USE) — an alternate cost (CR §1.5.5), the exert-based
 * sibling of `alternateCostBanish`: same per-candidate keyed option shape, enumerated by
 * `payments.ts` as its own `play:d…:exert:i…` option, but the cards named pay `{E}` instead of
 * being banished. Eligibility follows `{E}`'s own rule (CR §4.4.2): a character candidate must
 * be dry, an item or location need not be.
 */
export function alternateCostExert(
  exertable: Selector,
  options: { count?: number; window?: 'your-turn' | 'any-turn'; for?: 'play' | 'shift' } = {},
): StaticAbility {
  return staticAbility({
    kind: 'alternate-cost',
    payment: { kind: 'exert', filter: exertable },
    ...(options.count === undefined ? {} : { count: options.count }),
    ...(options.window === undefined ? {} : { window: options.window }),
    ...(options.for === undefined ? {} : { for: options.for }),
  });
}

/**
 * "You may put a [card] from your discard on the bottom of your deck to play this character
 * for free" — an alternate cost whose payment relocates a public discard card. Each legal
 * candidate is announced in its own keyed play option, then revalidated immediately before
 * payment just like the banish and exert variants.
 *
 * `for: 'shift'` is the ticket-136 sibling shape — "…to **shift** this character for free"
 * (Maleficent & Diablo - Evil Incarnate's FOOLS!) — where the payment substitutes for the
 * Shift cost rather than the ordinary play cost; `payments.ts` then pairs it with every legal
 * Shift base instead of offering it as a standalone free play.
 */
export function alternateCostMoveToBottomOfDeck(
  movable: Selector,
  options: { count?: number; window?: 'your-turn' | 'any-turn'; for?: 'play' | 'shift' } = {},
): StaticAbility {
  return staticAbility({
    kind: 'alternate-cost',
    payment: { kind: 'move-to-bottom-of-deck', filter: movable },
    ...(options.count === undefined ? {} : { count: options.count }),
    ...(options.window === undefined ? {} : { window: options.window }),
    ...(options.for === undefined ? {} : { for: options.for }),
  });
}

/**
 * "You may play [filter] for free" as a **continuous** permission (CR §6.1.7), re-evaluated on
 * every legality check — unlike `playForFree`, whose resolved effect leaves a floating record
 * with a duration. Wrap it in `whileActive(…)` for the printed condition, and pass
 * `from: 'hand'` for the shape that lives on the card being played (CR §6.1.12):
 *
 * ```ts
 * // "During your turn, you may play this character for free if an opposing character was
 * //  banished in a challenge this turn."
 * whileActive(
 *   allOf(duringYourTurn(), banishedInChallengeThisTurn(opposingCharacters())),
 *   mayPlayForFree(thisCard(), { from: 'hand' }),
 * )
 * ```
 */
export function mayPlayForFree(filter: Selector, options: { from?: Zone } = {}): StaticAbility {
  return staticAbility({
    kind: 'free-play',
    filter,
    ...(options.from === undefined ? {} : { from: options.from }),
  });
}

/**
 * "You may play [filter] from [zone], paying their full cost" — `mayPlayForFree`'s **paid**
 * generalization (ticket 140): the same continuous, live-rechecked permission machinery, naming
 * the zone the target card must be in (`mayPlayForFree` always meant `hand`) and pricing it at
 * the card's ordinary printed cost instead of nothing. `entersExerted` is "they enter play
 * exerted" — read at the point of play (`rules.ts`'s `playFromZonePermission`), not as a
 * separate `enter-play-ready` restriction, because that restriction is checked once the card is
 * already in `play` and can no longer tell it came from this permission rather than an ordinary
 * hand play. Wrap it in `whileActive(…)` for the printed condition:
 *
 * ```ts
 * // "While this character is exerted, you may play characters from your discard. If you do,
 * //  they enter play exerted. (You pay all costs.)"
 * whileActive(
 *   cardIs(self, { exerted: true }),
 *   mayPlayFromZone({ types: ['Character'], controller: 'you' }, 'discard', { entersExerted: true }),
 * )
 * ```
 */
export function mayPlayFromZone(
  filter: Selector,
  zone: Zone,
  options: { from?: Zone; entersExerted?: boolean } = {},
): StaticAbility {
  return staticAbility({
    kind: 'free-play',
    filter,
    zone,
    cost: 'full',
    ...(options.from === undefined ? {} : { from: options.from }),
    ...(options.entersExerted === undefined ? {} : { entersExerted: options.entersExerted }),
  });
}

/**
 * "If a card left a player's discard this turn, this card gains Shift 0" (Anna - Soothing
 * Sister's UNUSUAL TRANSFORMATION) — a conditional Shift grant the card gives **itself**,
 * re-evaluated on every legality check. Unlike `grantsKeyword('Shift', …)`, which only reaches
 * cards already in play, this functions from `hand` by default (CR §6.1.12) so it shows up in
 * ordinary play-option enumeration. Wrap it in `whileActive(…)` for the printed condition:
 *
 * ```ts
 * // "UNUSUAL TRANSFORMATION If a card left a player's discard this turn, this card gains
 * //  Shift 0."
 * whileActive(cardsLeftDiscardThisTurn(), gainsShift(0))
 * ```
 */
export function gainsShift(cost: number, options: { from?: Zone } = {}): StaticAbility {
  return staticAbility({
    kind: 'grant-shift',
    cost,
    ...(options.from === undefined ? {} : { from: options.from }),
  });
}

/**
 * "Your Broom characters gain Rush." `value` may be an `Amount` for a scaling grant — "gains
 * Resist +1 for each location you have in play" is `grantsKeyword(thisCard(), 'Resist',
 * count({ types: ['Location'], controller: 'you' }))` — counted continuously like `buff`'s.
 */
export function grantsKeyword(
  filter: Selector,
  keywordName: KeywordName,
  value?: Amount,
): StaticAbility {
  return staticAbility({
    kind: 'grant-keyword',
    filter,
    keyword: keywordName,
    ...(value === undefined ? {} : { value }),
  });
}

/**
 * "Your other characters gain the Detective classification" — the continuous mirror of
 * `grantSubtype`. Unlike `grantSubtype`'s applied, fixed-duration effect, this static is
 * re-derived on every read while the granting source stays in play (the same shape as
 * `grantsKeyword`), so a character that arrives later gets the classification too, and the
 * grant disappears the instant the source leaves play.
 */
export function grantsSubtype(filter: Selector, subtype: string): StaticAbility {
  return staticAbility({ kind: 'grant-subtype', filter, subtype });
}

/**
 * "Characters named Peter Pan lose Evasive and can't gain Evasive" — continuous keyword removal
 * (CR §6.6.5's "loses" pattern), the subtractive mirror of `grantsKeyword`. `keywordsOf`
 * (`keywords.ts`) filters lost keywords out *after* folding in every grant on every read, so the
 * denial half ("can't gain") needs no separate node: a later `grant-keyword`, applied or
 * continuous, never survives the filter while this static's `filter` still matches:
 *
 * ```ts
 * // Captain Hook - Master Swordsman ▸ MAN-TO-MAN
 * named('MAN-TO-MAN', losesKeyword({ types: ['Character'], name: 'Peter Pan' }, 'Evasive'))
 * ```
 */
export function losesKeyword(filter: Selector, keywordName: KeywordName): StaticAbility {
  return staticAbility({ kind: 'lose-keyword', filter, keyword: keywordName });
}

/**
 * "Your Gargoyle characters lose the Stone by Day ability" — continuous suppression of a
 * specifically **named** ability (CR §6.6.5's "loses" pattern), the subtractive mirror of
 * `grantsKeyword`. `name` is matched by normalized printed name (`modifiers.ts`'s
 * `normaliseAbilityName`), not display text, so it removes the named ability from a matching
 * card wherever it comes from — printed or granted — for as long as this static is live:
 *
 * ```ts
 * // Angela - Night Warrior ▸ ETERNAL NIGHT
 * named('ETERNAL NIGHT', suppresses(yourCharacters({ subtypes: ['Gargoyle'] }), 'STONE BY DAY'))
 * ```
 */
export function suppresses(filter: Selector, name: string): StaticAbility {
  return staticAbility({ kind: 'suppress-ability', filter, name });
}

// -- replacement effects (CR §6.5) -------------------------------------------

/**
 * The caught event's own amount — "put **that many** damage counters on this character
 * instead". `plus` offsets it, so `eventAmount(-1)` is "reduce that damage by 1"; the result
 * is floored at zero.
 */
export function eventAmount(plus?: number): EventAmount {
  return plus === undefined ? { kind: 'event-amount' } : { kind: 'event-amount', plus };
}

/** "… is dealt that damage instead" — a produced event, subject to Resist on its new target. */
export function dealDamageInstead(amount: EventAmount, target: CardRef): EventTemplate {
  return { kind: 'damage', verb: 'deal', target, amount };
}

/** "put that many damage counters on … instead" — no damage modifier applies. */
export function putDamageInstead(amount: EventAmount, target: CardRef): EventTemplate {
  return { kind: 'damage', verb: 'put', target, amount };
}

export function removeDamageInstead(amount: EventAmount, target: CardRef): EventTemplate {
  return { kind: 'damage', verb: 'remove', target, amount };
}

/** "move that many damage counters from … to … instead" — neither dealing nor putting. */
export function moveDamageInstead(
  amount: EventAmount,
  from: CardRef,
  to: CardRef,
): EventTemplate {
  return { kind: 'damage', verb: 'move', target: to, from, amount };
}

/**
 * The general replacement builder: "[event] **would** happen — [these] happen instead"
 * (CR §6.5). **Passing no templates prevents the event outright.**
 *
 * A replacement asks nobody anything, so its templates take plain `CardRef`s — `self` is the
 * card whose ability it is and `subject` is the event's affected card. It applies at most
 * once per event, and if the affected card is the one carrying it, before anybody else's.
 */
export function replacement(
  when: ReplacementCondition,
  ...into: EventTemplate[]
): ReplacementAbility {
  return { kind: 'replacement', when, into };
}

/** What every `whenWould…` helper narrows on — the replacement condition's scope slots. */
export interface ReplacementOptions {
  /** Whose card the event must be about. Default `self`. */
  readonly subject?: ReplacementCondition['subject'];
  /** A further predicate on the affected card ("one of your *other* characters"). */
  readonly filter?: Selector;
  readonly window?: 'your-turn' | 'opponent-turn' | 'any-turn';
  /** "The first time …, each turn" — see `ReplacementCondition.limit`. */
  readonly limit?: 'once-per-turn';
  readonly guard?: Condition;
  /** "**The next time** …, … instead" — see `ReplacementAbility.consumesOnApply`. */
  readonly consumesOnApply?: boolean;
}

/** Layers `consumesOnApply` onto a built replacement, only when the caller asked for it. */
function withConsumesOnApply(
  ability: ReplacementAbility,
  consumesOnApply: boolean | undefined,
): ReplacementAbility {
  return consumesOnApply === true ? { ...ability, consumesOnApply: true } : ability;
}

function replacementCondition(
  verbs: readonly DamageVerb[],
  options: ReplacementOptions,
): ReplacementCondition {
  return {
    event: 'damage',
    verbs,
    subject: options.subject ?? 'self',
    ...(options.filter === undefined ? {} : { filter: options.filter }),
    ...(options.window === undefined ? {} : { window: options.window }),
    ...(options.limit === undefined ? {} : { limit: options.limit }),
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  };
}

/**
 * "Whenever … **would be dealt** damage, … instead" — the `deal` verb only, so an effect that
 * *puts* counters on walks straight past it.
 *
 * ```ts
 * // Beast – Selfless Protector ▸ "Whenever one of your other characters would be dealt
 * // damage, put that many damage counters on this character instead."
 * whenWouldBeDealtDamage(
 *   { subject: 'controller', filter: { types: ['Character'], excludeSelf: true } },
 *   putDamageInstead(eventAmount(), self),
 * )
 * ```
 */
export function whenWouldBeDealtDamage(
  options: ReplacementOptions,
  ...into: EventTemplate[]
): ReplacementAbility {
  return withConsumesOnApply(
    replacement(replacementCondition(['deal'], options), ...into),
    options.consumesOnApply,
  );
}

/** "Whenever … **would take** damage" — dealt or put, the CR §1.9 umbrella. */
export function whenWouldBeDamaged(
  options: ReplacementOptions,
  ...into: EventTemplate[]
): ReplacementAbility {
  return withConsumesOnApply(
    replacement(replacementCondition(['deal', 'put'], options), ...into),
    options.consumesOnApply,
  );
}

/**
 * What a **player-scoped** replacement narrows on. A lore loss, a discard or a card entering
 * an inkwell happens to a player, not to a card, so there is no affected card for
 * `subject: 'self'` to name and no card for a `filter` to read — `defineAbilities` rejects
 * both.
 */
export interface PlayerReplacementOptions {
  /** Whose loss it must be. Default `controller` — "**you** can't lose lore". */
  readonly subject?: 'controller' | 'opponent' | 'any';
  readonly window?: 'your-turn' | 'opponent-turn' | 'any-turn';
  /** "The first time …, each turn" — see `ReplacementCondition.limit`. */
  readonly limit?: 'once-per-turn';
  readonly guard?: Condition;
  /** "**The next time** …, … instead" — see `ReplacementAbility.consumesOnApply`. */
  readonly consumesOnApply?: boolean;
}

function playerReplacementCondition(
  event: 'lose-lore' | 'discard' | 'gain-lore' | 'ink',
  options: PlayerReplacementOptions,
): ReplacementCondition {
  return {
    event,
    subject: options.subject ?? 'controller',
    ...(options.window === undefined ? {} : { window: options.window }),
    ...(options.limit === undefined ? {} : { limit: options.limit }),
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  };
}

/**
 * "… **would** lose lore, … instead" — and with no templates, "you can't lose lore".
 *
 * ```ts
 * // Koda – Talkative Cub ▸ "During opponents' turns, you can't lose lore."
 * whenWouldLoseLore({ window: 'opponent-turn' })
 * ```
 *
 * Lore *gained* is a different event and never comes through here, and neither does the lore
 * an opponent takes by questing — that is their gain, not your loss.
 */
export function whenWouldLoseLore(
  options: PlayerReplacementOptions = {},
  ...into: EventTemplate[]
): ReplacementAbility {
  return withConsumesOnApply(
    replacement(playerReplacementCondition('lose-lore', options), ...into),
    options.consumesOnApply,
  );
}

/**
 * "… **would** gain lore, … instead" — and with no templates, the gain is prevented outright.
 *
 * The mirror of `whenWouldLoseLore`, and it catches **every** source of a gain: questing, a
 * location's ◊ and an effect's "gain N lore" all pass through the one chokepoint the pipeline
 * is raised from. A conditional, player-wide "can't gain lore" is not this — that is
 * `cannotGainLore`, a continuous restriction rather than an "instead".
 */
export function whenWouldGainLore(
  options: PlayerReplacementOptions = {},
  ...into: EventTemplate[]
): ReplacementAbility {
  return withConsumesOnApply(
    replacement(playerReplacementCondition('gain-lore', options), ...into),
    options.consumesOnApply,
  );
}

/**
 * "If an effect **would** cause … to discard one or more cards from their hand, …" — and with
 * no templates, "you don't discard".
 *
 * The whole discard is one event, offered before anybody is asked *which* cards, so a
 * replacement cancels the requirement rather than a selection. A discard paid as a **cost** is
 * not an effect and never reaches this (CR §4.4).
 */
export function whenWouldDiscard(
  options: PlayerReplacementOptions = {},
  ...into: EventTemplate[]
): ReplacementAbility {
  return withConsumesOnApply(
    replacement(playerReplacementCondition('discard', options), ...into),
    options.consumesOnApply,
  );
}

/** "…enters exerted instead" — an `ink` replacement's own template (`enterInkwellExerted`). */
export function enterInkwellExerted(): EventTemplate {
  return { kind: 'ink', exerted: true };
}

/**
 * "Cards enter [whose] inkwells exerted" — the ordinary once-per-turn ink action
 * (`inkEvent`, `damage.ts`) **would** put the card in ready, and this makes it enter exerted
 * instead. With no templates the ink action is prevented outright, which no printed card asks
 * for today but the shape allows.
 *
 * ```ts
 * // Daisy Duck - Paranormal Investigator ▸ "STRANGE HAPPENINGS While this character is
 * // exerted, cards enter opponents' inkwells exerted."
 * whenWouldEnterInkwell(
 *   { subject: 'opponent', guard: cardIs(self, { exerted: true }) },
 *   enterInkwellExerted(),
 * )
 * ```
 */
export function whenWouldEnterInkwell(
  options: PlayerReplacementOptions = {},
  ...into: EventTemplate[]
): ReplacementAbility {
  return replacement(playerReplacementCondition('ink', options), ...into);
}

function banishReplacementCondition(options: ReplacementOptions): ReplacementCondition {
  return {
    event: 'banish',
    subject: options.subject ?? 'self',
    ...(options.filter === undefined ? {} : { filter: options.filter }),
    ...(options.window === undefined ? {} : { window: options.window }),
    ...(options.limit === undefined ? {} : { limit: options.limit }),
    ...(options.guard === undefined ? {} : { guard: options.guard }),
  };
}

/**
 * "If [this character] would be banished, [something] instead" (CR §6.5, ticket 137) — and
 * with no templates, the banish is prevented outright. Card-scoped like a damage replacement
 * (`subject`/`filter` read the character that would be banished, not a player), so it shares
 * `ReplacementOptions` rather than `PlayerReplacementOptions`; default `subject` is `self`,
 * matching every printed banish replacement so far ("if **this character** would be banished").
 *
 * ```ts
 * // Mickey Mouse & Minnie Mouse - Adventuring Duo ▸ "THINKING OF YOU If this character would
 * // be banished, put them into your inkwell facedown and exerted instead."
 * whenWouldBeBanished({}, enterInkwellInstead({ facedown: true }))
 * ```
 */
export function whenWouldBeBanished(
  options: ReplacementOptions,
  ...into: EventTemplate[]
): ReplacementAbility {
  return withConsumesOnApply(
    replacement(banishReplacementCondition(options), ...into),
    options.consumesOnApply,
  );
}

/**
 * "…put them into your inkwell … instead" — a `banish` replacement's own template. Enters
 * exerted by default (matching the ordinary ink action's own default of ready being what the
 * card explicitly overrides); `facedown` defaults to `false`, since no printed card needs
 * anything else yet.
 */
export function enterInkwellInstead(
  options: { readonly exerted?: boolean; readonly facedown?: boolean } = {},
): EventTemplate {
  return {
    kind: 'to-inkwell',
    ...(options.exerted === undefined ? {} : { exerted: options.exerted }),
    ...(options.facedown === undefined ? {} : { facedown: options.facedown }),
  };
}

// ---------------------------------------------------------------------------
// Assembling a card's IR
// ---------------------------------------------------------------------------

/** A card's abilities failed the build-time checks in `defineAbilities`. */
export class AbilityDefinitionError extends Error {}

/**
 * Validate and freeze a card's abilities. This is the DSL's last gate before the IR becomes
 * data: it rejects duplicate target bindings, unknown binding references and empty effects,
 * so a mistake surfaces when the module loads rather than mid-game.
 */
export function defineAbilities(abilities: readonly Ability[]): readonly Ability[] {
  for (const ability of abilities) {
    if (ability.kind === 'triggered' || ability.kind === 'activated' || ability.kind === 'action') {
      checkEffects(ability.effect, ability.name ?? ability.kind);
    }
    if (ability.kind === 'replacement') checkReplacement(ability);
  }
  return abilities;
}

/**
 * A replacement resolves inside the event it caught, without a resolution stack frame and
 * therefore without asking anyone anything — so a template naming a chosen card could never
 * be satisfied. Reject it here rather than mid-game.
 */
function checkReplacement(ability: ReplacementAbility): void {
  const where = ability.name ?? 'replacement';
  const when = ability.when;
  // Card-scoped events (`damage`, `banish`) have an affected card; every other event is
  // player-scoped and has no affected *card*, so every slot that reads one is a mistake the
  // matcher could only ever answer "no" to — say so at load time instead.
  const cardScoped = when.event === 'damage' || when.event === 'banish';
  if (!cardScoped) {
    if (when.subject === 'self') {
      throw new AbilityDefinitionError(
        `${where}: a ${when.event} replacement has no affected card, so its subject cannot be "self"`,
      );
    }
    if (when.filter !== undefined) {
      throw new AbilityDefinitionError(
        `${where}: a ${when.event} replacement has no affected card for a filter to read`,
      );
    }
  }
  // Verbs are damage's own taxonomy — meaningless on any other event, `banish` included.
  if (when.event !== 'damage' && when.verbs !== undefined) {
    throw new AbilityDefinitionError(`${where}: damage verbs do not apply to ${when.event}`);
  }
  for (const template of ability.into) {
    // An `ink` template carries no `CardRef`s at all — it just flips the caught ink event's
    // own `exerted` flag — and is only meaningful re-raising the same kind of event it caught.
    if (template.kind === 'ink') {
      if (when.event !== 'ink') {
        throw new AbilityDefinitionError(
          `${where}: only an ink replacement can produce an ink event`,
        );
      }
      continue;
    }
    // A `to-inkwell` template is the same shape of no-`CardRef` shorthand, only legal catching
    // the `banish` it redirects.
    if (template.kind === 'to-inkwell') {
      if (when.event !== 'banish') {
        throw new AbilityDefinitionError(
          `${where}: only a banish replacement can produce a to-inkwell event`,
        );
      }
      continue;
    }
    // `subject` names the caught event's affected card, which a player-scoped event lacks.
    if (!cardScoped) {
      for (const ref of [template.target, template.from]) {
        if (ref?.kind !== 'subject') continue;
        throw new AbilityDefinitionError(
          `${where}: a ${when.event} replacement has no affected card to call "subject"`,
        );
      }
    }
    for (const ref of [template.target, template.from]) {
      if (ref?.kind !== 'target') continue;
      throw new AbilityDefinitionError(
        `${where}: a replacement cannot name the chosen target "${ref.id}" — it makes no choices`,
      );
    }
    if (template.verb === 'move' && template.from === undefined) {
      throw new AbilityDefinitionError(`${where}: moving damage needs a card to move it from`);
    }
  }
}

/**
 * Bindings the **engine** supplies rather than a player's targeting choice: cards the event
 * named (`bag.ts`'s `eventBindings` — the card a `card-sung` event's singer sang, `song`
 * (ticket 127), a sung song's `singers`, or a banish's pre-dissolve pile, `under-cards`,
 * ticket 104) and cards an activation cost took (`interpret.ts`'s `startActivation` — a
 * "Banish one of your … —" cost's `cost-banished`). In scope for any ability, because only the
 * machinery that produced them could have named them: an ability that reads one and arrived
 * some other way simply finds nothing bound, which is the ordinary "do as much as you can"
 * (CR §1.2.3).
 */
export const EVENT_BINDINGS: readonly string[] = ['song', 'singers', 'cost-banished', 'under-cards'];

/**
 * The binding id an effect declares for **later siblings in the same sequence** — the only
 * kind today is `put-under-from-deck`'s `as` (ticket 116): it names whichever members of its
 * `scope` actually received a card, so a following sibling ("… those characters gain Ward…")
 * can read it back with `bound(id)`. Unlike a `chosen.…({ as })` pick, which nests everything
 * that uses it inside its own `then`, this binding is produced by resolution rather than by a
 * player's choice — closer in kind to `EVENT_BINDINGS` than to a targeting closure — so it is
 * scoped forward through the sequence instead of downward through a wrapper.
 */
function declaredBinding(effect: Effect): string | undefined {
  return effect.kind === 'put-under-from-deck' ? effect.as : undefined;
}

/** Fold a step's own binding (if it declares one) into the running scope, once checked. */
function foldDeclared(step: Effect, where: string, scope: readonly string[]): readonly string[] {
  const declared = declaredBinding(step);
  if (declared === undefined) return scope;
  if (scope.includes(declared)) {
    throw new AbilityDefinitionError(
      `${where}: two bindings are both named "${declared}" — name one differently`,
    );
  }
  return [...scope, declared];
}

function checkEffects(effects: readonly Effect[], where: string): void {
  let scope: readonly string[] = EVENT_BINDINGS;
  for (const effect of effects) {
    checkEffect(effect, where, scope);
    scope = foldDeclared(effect, where, scope);
  }
}

function checkEffect(effect: Effect, where: string, inScope: readonly string[]): void {
  switch (effect.kind) {
    case 'target': {
      if (inScope.includes(effect.spec.id)) {
        throw new AbilityDefinitionError(
          `${where}: two targets are both named "${effect.spec.id}" — name one with { as: … }`,
        );
      }
      checkEffect(effect.then, where, [...inScope, effect.spec.id]);
      return;
    }
    // A `choose-player` binding lives in its own namespace (`PlayerRef { kind: 'chosen' }`,
    // never a `CardRef`), so it does not extend `inScope` — but `then` still has to be walked
    // structurally rather than falling to `default`'s shallow `refsOf`, which would mistake the
    // nested node's own `kind` for a bare `CardRef` (ticket 102).
    case 'choose-player':
      checkEffect(effect.then, where, inScope);
      return;
    // Same reason as `choose-player`, one node up: `each-player`'s own binding lives in the
    // `PlayerRef { kind: 'chosen' }` namespace, not `inScope`'s `CardRef` one, but `then` still
    // needs a real structural walk (ticket 125).
    case 'each-player':
      checkEffect(effect.then, where, inScope);
      return;
    case 'seq': {
      let scope = inScope;
      for (const step of effect.steps) {
        checkEffect(step, where, scope);
        scope = foldDeclared(step, where, scope);
      }
      return;
    }
    case 'may':
      checkEffect(effect.then, where, inScope);
      if (effect.otherwise !== undefined) checkEffect(effect.otherwise, where, inScope);
      return;
    case 'modal':
      for (const mode of effect.modes) checkEffect(mode.effect, where, inScope);
      return;
    case 'if':
      checkEffect(effect.then, where, inScope);
      if (effect.otherwise !== undefined) checkEffect(effect.otherwise, where, inScope);
      return;
    default: {
      for (const ref of refsOf(effect)) {
        if (ref.kind === 'target' && !inScope.includes(ref.id)) {
          throw new AbilityDefinitionError(
            `${where}: effect refers to target "${ref.id}", which nothing chose`,
          );
        }
      }
      return;
    }
  }
}

/** Every `CardRef` a leaf effect names, including the ones inside its `Amount`. */
function refsOf(effect: Effect): CardRef[] {
  const found: CardRef[] = [];
  const record = effect as unknown as Record<string, unknown>;
  for (const value of Object.values(record)) {
    if (value === null || typeof value !== 'object') continue;
    const node = value as Record<string, unknown>;
    if (typeof node['kind'] !== 'string') continue;
    const kind = node['kind'] as string;
    if (kind === 'target' || kind === 'self' || kind === 'subject' || kind === 'instance') {
      found.push(node as unknown as CardRef);
    } else if ('of' in node && typeof node['of'] === 'object' && node['of'] !== null) {
      const inner = node['of'] as Record<string, unknown>;
      if (typeof inner['kind'] === 'string') found.push(inner as unknown as CardRef);
    }
  }
  return found;
}
